/**
 * ActionFlowEngine v2 — strict flow execution with stateContract enforcement.
 *
 * Key differences from v1:
 * - NO alias handling (only "params", "target", "append" — no "config", "path", "push")
 * - stateContract enforced on every setState
 * - Simplified action set (same builtins, strict param names)
 */

import type { Flow, FlowDuplicatePolicy, FlowStep, BasicStep, ConditionalStep, LoopStep, ParallelStep, TryCatchStep, SwitchStep, StageStep, SagaStep } from "@cogo/types-v2"
import type { GeminiContent } from "./gemini"
import { GeminiClient, ToolCallbackHandler, buildFunctionDeclarations } from "./gemini"
import type { PlatformAdapter as FullPlatformAdapter } from "./platform-adapter-base"
import { StateManager } from "./state-manager"
import { ComputeEvaluator, isTruthy } from "./compute-evaluator"
import { StoreActionRegistry } from "./store-action-registry"
import type { DataSourceManager } from "./datasource-manager"

export type ToastHandler = (params: { message: string; description?: string; variant?: string; durationMs?: number }) => void

export const ACTION_FLOW_TRACE_CONTRACT_VERSION = "actionflow-trace.v1" as const

export type ActionFlowExecutionStatus =
  | "ok"
  | "stopped"
  | "error"
  | "cancelled"
  | "timed-out"
  | "deduplicated"

export type ActionFlowExecutionErrorCode =
  | "ACTIONFLOW_CANCELLED"
  | "ACTIONFLOW_TIMEOUT"
  | "ACTIONFLOW_DUPLICATE"

export interface ActionFlowExecutionOptions {
  /** Host-owned cancellation signal. */
  signal?: AbortSignal
  /** Overrides flow.execution.timeoutMs. */
  timeoutMs?: number
  /** Overrides flow.execution.duplicatePolicy. */
  duplicatePolicy?: FlowDuplicatePolicy
  /** Overrides and may pre-resolve flow.execution.idempotencyKey. */
  idempotencyKey?: string
  /** Overrides flow.execution.idempotencyWindowMs. */
  idempotencyWindowMs?: number
  /** Optional caller-supplied correlation identifier. */
  executionId?: string
}

export class ActionFlowExecutionError extends Error {
  readonly code: ActionFlowExecutionErrorCode
  readonly executionId: string

  constructor(code: ActionFlowExecutionErrorCode, executionId: string, message: string) {
    super(message)
    this.name = "ActionFlowExecutionError"
    this.code = code
    this.executionId = executionId
  }
}

export interface ActionFlowStateChange {
  path: string
  before: unknown
  after: unknown
}

export interface ActionFlowStepTrace {
  index: number
  parentIndex?: number
  /** Syntactic position of this step in the flow definition (e.g.
   * `steps.0.then.1`, `steps.2.steps.0`) — matches the path format of the
   * Studio static step evidence. Loop iterations repeat the same path;
   * untaken branches never appear. Consumers pair executed steps to flow
   * structure by this instead of a flat executed-order ordinal (which
   * drifts under loops/branches). */
  path?: string
  type: string
  name?: string
  action?: string
  params?: Record<string, unknown>
  detail?: Record<string, unknown>
  status: "ok" | "skipped" | "error"
  result?: unknown
  error?: string
  stateChanges: ActionFlowStateChange[]
}

export interface ActionFlowExecutionTrace {
  contractVersion: typeof ACTION_FLOW_TRACE_CONTRACT_VERSION
  traceId: string
  executionId: string
  flowName?: string
  trigger: Flow["trigger"]
  startedAt: string
  finishedAt?: string
  durationMs?: number
  idempotencyKey?: string
  duplicateOfExecutionId?: string
  startState: Record<string, unknown>
  finalState: Record<string, unknown>
  steps: ActionFlowStepTrace[]
  status: ActionFlowExecutionStatus
  error?: string
  /** Optional diagnostic reason when status === "stopped" (from stopFlow's `reason`). */
  stopReason?: string
}

type StepTraceHandle = {
  entry: ActionFlowStepTrace
  before: Record<string, unknown>
} | null

interface StageExecutionResult {
  /** True only when onEnter, body, and onSuccess all completed. */
  completed: boolean
  trace: StepTraceHandle
}

interface ActionFlowExecutionControl {
  signal: AbortSignal
  executionId: string
  timedOut: boolean
}

interface ResolvedActionFlowExecutionPolicy {
  executionId: string
  timeoutMs?: number
  duplicatePolicy: FlowDuplicatePolicy
  idempotencyKey?: string
  idempotencyWindowMs: number
  executionKey?: string
}

interface ActiveActionFlowExecution {
  executionId: string
  promise: Promise<void>
  controller: AbortController
}

interface CompletedActionFlowExecution {
  executionId: string
  expiresAt: number
}

const ACTION_FLOW_EXECUTION_CONTROL_CONTEXT_KEY = "__cogoActionFlowExecutionControl"
let actionFlowExecutionSequence = 0

/**
 * v2 PlatformAdapter — re-exports v1 full interface (27 methods).
 * Implementations: WebPlatformAdapter, NativePlatformAdapter, NoopAdapter.
 * Optional methods (openAuthSession) are added as extension.
 */
export interface PlatformAdapter extends FullPlatformAdapter {
  /** OAuth session via in-app browser (RN: expo-web-browser, Web: popup) */
  openAuthSession?(url: string, redirectUrl?: string): Promise<{ type: string; url?: string }>
}

/**
 * Runtime action-name aliases — mirrors `runtimeCompatRules.actionNameAliases`
 * in packages/cogo-validator/ontology/action-types.json. Legacy spellings still
 * emitted by existing block flows are normalized to the canonical builtin name
 * at dispatch (the generation enum + closure gates key off the canonical names).
 * Without this, the ~13 blocks-v2 flows that emit `loadDataSource` (capital S)
 * would miss the `loadDatasource` builtin and be silently skipped.
 */
const ACTION_NAME_ALIASES: Record<string, string> = {
  loadDataSource: "loadDatasource",
  // Legacy Flutter-era REST step. Same param surface as restApi (url/method/
  // headers/body/timeout per @cogo/schema actions.json restApi.flutter mapping);
  // the one rename, responseKey → resultTarget, is read-tolerated by
  // actionRestApi. The previous target `restful_data` was a ghost — no builtin,
  // no registerAction call site — so every legacy REST step silently skipped.
  // NOTE: comments inside this object must avoid curly braces — the
  // emit-actionflow-bridge alias parser captures this literal with a lazy
  // regex that stops at the first closing brace.
  restful_data_action: "restApi",
  emit: "emitEvent",
  callback_action: "invokeCallback",
  callback: "invokeCallback",
}

/** Per-execution context key used by host effects that must stay block-scoped. */
export const ACTION_FLOW_BLOCK_NAMESPACE_CONTEXT_KEY = "__cogoBlockNamespace"

/**
 * Lane B-4 meta-actions that dispatch nested sub-actions. executeBasic keeps their
 * sub-action-bearing fields RAW (the sub-actions are re-dispatched through
 * executeBasic, which re-resolves each — preserving setState `#{item.*}` per-item
 * scope that a one-shot deep resolve here would otherwise collapse).
 */
const META_SUBACTION_ACTIONS = new Set<string>([
  "batchAction", "queueAction", "retryAction", "throttleAction", "debounceAction", "transactionAction",
])

export class ActionFlowEngine {
  private state: StateManager
  private compute: ComputeEvaluator
  private builtinActions: Record<string, (params: Record<string, unknown>, context?: Record<string, unknown>) => unknown | Promise<unknown>>
  private debounceTimers = new Map<string, ReturnType<typeof setTimeout>>()
  // Flow-level debounce: the superseded call's resolver, settled on coalesce —
  // mirrors actionDebounceResolvers below. Clearing only the timer left the
  // earlier executeFlow() promise pending forever, hanging any awaiter (incl.
  // the Studio trace wrapper, which then stayed permanently unwrapped).
  private flowDebounceResolvers = new Map<string, () => void>()
  // Lane B-4 execution-control state (ported from v1 @cogo/runtime-core).
  private actionQueues = new Map<string, Array<{ priority: number; task: () => Promise<void> }>>()
  private queueDraining = new Set<string>()
  private throttleTimestamps = new Map<string, number>()
  private actionDebounceTimers = new Map<string, ReturnType<typeof setTimeout>>()
  private actionDebounceResolvers = new Map<string, () => void>()
  // lodash-style debounce extras: the maxWait ceiling timer per key, and the
  // "a call is awaiting a trailing invoke" flag per key (cleared once served by a
  // leading edge, trailing fire, or maxWait fire).
  private actionDebounceMaxTimers = new Map<string, ReturnType<typeof setTimeout>>()
  private actionDebouncePendingTrailing = new Set<string>()
  // The latest call's sub-action invoker per key — so a maxWait-forced invoke fires
  // with the most-recent args (like lodash / the trailing edge), not the leading
  // call's stale closure.
  private actionDebounceLatestRun = new Map<string, () => Promise<void>>()
  private activeExecutions = new Map<string, ActiveActionFlowExecution>()
  private completedExecutions = new Map<string, CompletedActionFlowExecution>()
  private flowRegistry: Record<string, Flow> = {}
  /** Registered full flow name by flow object, used to carry block scope across awaits. */
  private registeredFlowNames = new WeakMap<object, string>()
  /** Cache of flows subscribed to an app-event via `flow.on` (null = rebuild). Invalidated on registerFlows. */
  private appEventFlows: Map<string, Flow[]> | null = null
  private platform: PlatformAdapter | null = null
  private storeActionRegistry: StoreActionRegistry | null = null
  private datasources: DataSourceManager | null = null
  private activeTrace: ActionFlowExecutionTrace | null = null
  private traceStepIndex = 0
  private traceParentStack: number[] = []

  onToast: ToastHandler | null = null
  onShowPopup: ((params: { title: string; message: string; type?: string; actions?: unknown[] }) => void) | null = null
  onShowLoading: ((show: boolean, options?: { message?: string; overlay?: boolean }) => void) | null = null
  // Lane B-3: app-event bus. The host wires this to fan a named app event out to
  // subscribers (sibling blocks / host components). Null = headless no-op.
  onEmitEvent: ((name: string, payload?: unknown) => void) | null = null
  // Lane B-5: named-modal opener. The host wires a modal-registry-by-id; the
  // executor lifts the corpus {popup,...rest} into (modalId, props). Null = no-op.
  onOpenModal: ((modalId: string, props: Record<string, unknown>) => void) | null = null
  // Lane B-5: block-instance-scoped named host-callback invoker (corpus
  // callback_action onApprove/onReject). The host wires a per-block handler
  // registry; a global hook would shadow across cards. Null = headless no-op.
  onInvokeCallback: ((name: string, args?: unknown, context?: Record<string, unknown>) => void) | null = null
  // Lane B-7: media-element controller (corpus video-player play/pause/seek/
  // volume/fullscreen). Block-instance-scoped — a global hook would command the
  // wrong <video> when multiple players are mounted. Null = headless no-op.
  onMediaControl: ((command: string, args: Record<string, unknown>, context?: Record<string, unknown>) => void) | null = null
  // Presentation action with an explicit completion barrier. The host returns a
  // promise that settles when the visual motion finishes; executeBasic already
  // awaits action results, so stage/saga ordering stays deterministic.
  onRunMotion: ((params: Record<string, unknown>, context?: Record<string, unknown>) => unknown | Promise<unknown>) | null = null

  constructor(state: StateManager, platform?: PlatformAdapter) {
    this.state = state
    this.compute = state.getCompute()
    this.platform = platform ?? null
    this.builtinActions = {
      setState: (params) => this.actionSetState(params),
      navigate: (params) => this.actionNavigate(params),
      showToast: (params) => this.actionShowToast(params),
      delay: (params, ctx) => this.actionDelay(params, ctx),
      restApi: (params, ctx) => this.actionRestApi(params, ctx),
      "datasource.call": (params) => this.actionDatasourceCall(params),
      setVariable: (params) => this.actionSetVariable(params),
      log: (params) => this.actionLog(params),
      stopFlow: (params) => this.actionStopFlow(params),
      callFlow: (params, ctx) => this.actionCallFlow(params, ctx),
      compute: (params) => this.actionCompute(params),
      computeDateRange: (params) => this.actionComputeDateRange(params),
      showPopup: (params) => this.actionShowPopup(params),
      showLoading: (params) => this.actionShowLoading(params),
      goLink: (params) => this.actionGoLink(params),
      copyToClipboard: (params) => this.actionCopyToClipboard(params),
      scrollToPosition: (params) => this.actionScrollToPosition(params),
      scrollToElement: (params) => this.actionScrollToElement(params),
      setTheme: (params) => this.actionSetTheme(params),
      emitEvent: (params) => this.actionEmitEvent(params),
      runMotion: (params, ctx) => this.actionRunMotion(params, ctx),
      openModal: (params) => this.actionOpenModal(params),
      invokeCallback: (params, ctx) => this.actionInvokeCallback(params, ctx),
      geminiAction: (params) => this.actionGemini(params),
      geminiFunctionCall: (params) => this.actionGeminiFunctionCall(params),
      geminiVision: (params) => this.actionGeminiVision(params),
      // ── Platform-delegated actions (v18.0 cross-platform parity) ──
      storage: (params) => this.actionStorage(params),
      clipboardRead: (params) => this.actionClipboardRead(params),
      shareAction: (params) => this.actionShare(params),
      geolocation: (params) => this.actionGeolocation(params),
      downloadAction: (params) => this.actionDownload(params),
      fileUpload: (params) => this.actionFileUpload(params),
      notification: (params) => this.actionNotification(params),
      cryptoAction: (params) => this.actionCrypto(params),
      printAction: () => this.actionPrint(),
      analyticsEvent: (params) => this.actionAnalytics(params),
      openAuthSession: (params) => this.actionOpenAuthSession(params),
      payment: (params) => this.actionPayment(params),
      mediaCapture: (params) => this.actionMediaCapture(params),
      websocket: (params) => this.actionWebSocket(params),
      dispatchStoreAction: (params) => this.actionDispatchStoreAction(params),
      // Aliases: flow.json may use these as standalone action names
      // instead of setState with operation parameter
      upsertWhere: (params) => this.actionSetState({ ...params, operation: "upsert", match: params.matchExpr }),
      removeWhere: (params) => this.actionSetState({ ...params, operation: "removeWhere" }),
      updateWhere: (params) => this.actionSetState({ ...params, operation: "updateWhere", update: params.updates }),
      validate: (params) => this.actionValidate(params),
      // ── Data / table mutation actions (ported from v1 @cogo/runtime-core) ──
      groupBy: (params) => this.actionGroupBy(params),
      computeField: (params) => this.actionComputeField(params),
      setTableCell: (params) => this.actionSetTableCell(params),
      addTableRow: (params) => this.actionAddTableRow(params),
      deleteTableRow: (params) => this.actionDeleteTableRow(params),
      sortByField: (params) => this.actionSortByField(params),
      // ── Interactive confirmation (speced: actions-v1.ttl confirmAction) ──
      confirmAction: (params) => this.actionConfirmAction(params),
      // ── Lane B-2: export + web3 executors (close action-surface quarantine) ──
      csvExport: (params) => this.actionCsvExport(params),
      jsonExport: (params) => this.actionJsonExport(params),
      web3: (params) => this.actionWeb3(params),
      // ── Lane B-3: datasource resolver (delegates to injected DataSourceManager) ──
      loadDatasource: (params) => this.actionLoadDatasource(params),
      // ── Lane B-4: execution-control + graphql executors (ported from v1) ──
      // The meta-actions forward the execution `context` to dispatched
      // sub-actions so nested callFlow / #{event.*} / loop vars resolve correctly.
      graphqlAction: (params) => this.actionGraphql(params),
      batchAction: (params, ctx) => this.actionBatch(params, ctx),
      conditionalDelay: (params) => this.actionConditionalDelay(params),
      queueAction: (params, ctx) => this.actionQueue(params, ctx),
      retryAction: (params, ctx) => this.actionRetry(params, ctx),
      throttleAction: (params, ctx) => this.actionThrottle(params, ctx),
      debounceAction: (params, ctx) => this.actionDebounce(params, ctx),
      transactionAction: (params, ctx) => this.actionTransaction(params, ctx),
      // ── Lane B-6: file pickers + multipart upload + image crop ──
      pickFiles: (params) => this.actionPickFiles(params),
      openNativeFilePicker: (params) => this.actionOpenNativeFilePicker(params),
      uploadFiles: (params) => this.actionUploadFiles(params),
      cropImage: (params) => this.actionCropImage(params),
      // ── Lane B-7: media-element controller (video-player) ──
      mediaControl: (params, ctx) => this.actionMediaControl(params, ctx),
    }
  }

  /** Resolve a builtin by name for meta-actions, honoring legacy-name aliases. */
  private resolveBuiltin(name: string) {
    return this.builtinActions[ACTION_NAME_ALIASES[name] ?? name]
  }

  /**
   * Dispatch a meta-action's nested sub-action through executeBasic so its params
   * are resolved per sub-action (incl. the setState `#{item.*}` per-item scope that
   * must NOT be pre-resolved by the meta-action's own param resolution).
   */
  private async dispatchSubAction(action: string, params: Record<string, unknown>, context?: Record<string, unknown>): Promise<void> {
    await this.executeBasic({ type: "basic", action, params } as BasicStep, context ?? {})
  }

  /**
   * Normalize a single-sub-action selector for the execution-control meta-actions.
   * The contracts disagree: @cogo/action-registry uses a string `action` + object
   * `params`; the TBox cschema (e.g. debounceAction) shapes `action` as the nested
   * object `{ action, params }`. Accept both forms.
   */
  private normalizeSubAction(actionField: unknown, paramsField: unknown): { name?: string; params: Record<string, unknown> } {
    if (actionField && typeof actionField === "object") {
      const o = actionField as { action?: string; params?: Record<string, unknown> }
      return { name: typeof o.action === "string" ? o.action : undefined, params: o.params ?? {} }
    }
    return {
      name: typeof actionField === "string" ? actionField : undefined,
      params: (paramsField as Record<string, unknown>) ?? {},
    }
  }

  /** Expose state interface for external action registrations (e.g., Supabase) per D-03 */
  get stateAccessor(): { setState(path: string, value: unknown): void; resolve(template: string): unknown } {
    return {
      setState: (path, value) => this.state.setState(path, value),
      resolve: (template) => this.compute.evaluate(template),
    }
  }

  /** Expose compute interface for external action registrations */
  get computeAccessor(): { evaluate(expr: string): unknown } {
    return this.compute
  }

  setPlatform(platform: PlatformAdapter): void {
    this.platform = platform
  }

  /**
   * Attach a StoreActionRegistry so cogo JSON flows can invoke host store
   * actions via `dispatchStoreAction` with a string handler name.
   * Pass `null` to detach (e.g. on unmount).
   */
  setStoreActionRegistry(registry: StoreActionRegistry | null): void {
    this.storeActionRegistry = registry
  }

  getStoreActionRegistry(): StoreActionRegistry | null {
    return this.storeActionRegistry
  }

  /**
   * Attach a DataSourceManager so the `loadDatasource` action can resolve named
   * block/app datasources at runtime. Pass `null` to detach. Without one, the
   * action is a no-op (the host wires this from the manifest/block sources).
   */
  setDataSourceManager(manager: DataSourceManager | null): void {
    this.datasources = manager
  }

  getDataSourceManager(): DataSourceManager | null {
    return this.datasources
  }

  registerFlows(flows: Record<string, Flow>): void {
    Object.assign(this.flowRegistry, flows)
    for (const [name, flow] of Object.entries(flows)) {
      if (flow && typeof flow === "object") this.registeredFlowNames.set(flow, name)
    }
    this.appEventFlows = null // flow set changed → rebuild the app-event subscription map
  }

  /** Flows subscribed to each app-event name via `flow.on` (built lazily, cached). */
  private getAppEventFlows(): Map<string, Flow[]> {
    if (this.appEventFlows) return this.appEventFlows
    const map = new Map<string, Flow[]>()
    for (const key of Object.keys(this.flowRegistry)) {
      const flow = this.flowRegistry[key]
      const on = (flow as { on?: unknown }).on
      if (typeof on === "string" && on) {
        const arr = map.get(on)
        if (arr) arr.push(flow)
        else map.set(on, [flow])
      }
    }
    this.appEventFlows = map
    return map
  }

  getRegisteredFlowNames(): string[] {
    return Object.keys(this.flowRegistry)
  }

  findFlow(flowName: string): Flow | undefined {
    return this.flowRegistry[flowName]
      ?? Object.entries(this.flowRegistry).find(([k]) => k.endsWith(`.${flowName}`))?.[1]
  }

  async executeFlow(
    flow: Flow,
    context?: Record<string, unknown>,
    options: ActionFlowExecutionOptions = {},
  ): Promise<void> {
    const executionContext = this.contextForFlow(flow, context)
    const policy = this.resolveExecutionPolicy(flow, executionContext, options)
    const duplicate = this.findDuplicateExecution(policy)
    if (duplicate) {
      if (policy.duplicatePolicy === "reject") {
        throw duplicateExecutionError(policy, duplicate.executionId)
      }
      if (duplicate.promise) await duplicate.promise
      return
    }

    const controller = new AbortController()
    const control: ActionFlowExecutionControl = {
      signal: controller.signal,
      executionId: policy.executionId,
      timedOut: false,
    }
    const executionCanBeCancelled = Boolean(
      options.signal ||
      policy.timeoutMs ||
      (policy.executionKey && policy.duplicatePolicy !== "parallel"),
    )
    const controlledContext = executionCanBeCancelled
      ? {
          ...executionContext,
          [ACTION_FLOW_EXECUTION_CONTROL_CONTEXT_KEY]: control,
        }
      : executionContext
    const abortFromHost = () => {
      if (controller.signal.aborted) return
      controller.abort(
        options.signal?.reason ??
          new ActionFlowExecutionError(
            "ACTIONFLOW_CANCELLED",
            policy.executionId,
            `ActionFlow execution "${policy.executionId}" was cancelled by its host.`,
          ),
      )
    }
    if (options.signal?.aborted) abortFromHost()
    else options.signal?.addEventListener("abort", abortFromHost, { once: true })

    let timeout: ReturnType<typeof setTimeout> | undefined
    if (policy.timeoutMs) {
      timeout = setTimeout(() => {
        if (controller.signal.aborted) return
        control.timedOut = true
        controller.abort(
          new ActionFlowExecutionError(
            "ACTIONFLOW_TIMEOUT",
            policy.executionId,
            `ActionFlow execution "${policy.executionId}" exceeded ${policy.timeoutMs}ms.`,
          ),
        )
      }, policy.timeoutMs)
    }

    const work = this.executeFlowScheduled(flow, controlledContext)
    const controlledWork = flow.debounce
      ? this.raceWithExecutionControl(work, controlledContext)
      : work
    const managedPromise = controlledWork
      .then(() => {
        if (policy.executionKey && policy.idempotencyWindowMs > 0) {
          this.completedExecutions.set(policy.executionKey, {
            executionId: policy.executionId,
            expiresAt: Date.now() + policy.idempotencyWindowMs,
          })
        }
      })
      .finally(() => {
        if (timeout) clearTimeout(timeout)
        options.signal?.removeEventListener("abort", abortFromHost)
        if (
          policy.executionKey &&
          this.activeExecutions.get(policy.executionKey)?.executionId ===
            policy.executionId
        ) {
          this.activeExecutions.delete(policy.executionKey)
        }
      })

    if (policy.executionKey && policy.duplicatePolicy !== "parallel") {
      this.activeExecutions.set(policy.executionKey, {
        executionId: policy.executionId,
        promise: managedPromise,
        controller,
      })
    }
    return managedPromise
  }

  private executeFlowScheduled(
    flow: Flow,
    executionContext: Record<string, unknown>,
  ): Promise<void> {
    if (flow.debounce && flow.debounce > 0 && flow.name) {
      return new Promise<void>((resolve, reject) => {
        const key = flow.name!
        const existing = this.debounceTimers.get(key)
        if (existing) {
          clearTimeout(existing)
          // Settle the superseded call — its work is coalesced into this one.
          this.flowDebounceResolvers.get(key)?.()
        }
        this.flowDebounceResolvers.set(key, resolve)
        this.debounceTimers.set(
          key,
          setTimeout(async () => {
            this.debounceTimers.delete(key)
            this.flowDebounceResolvers.delete(key)
            try {
              await this.executeFlowImmediate(flow, executionContext)
              resolve()
            } catch (error) {
              reject(error)
            }
          }, flow.debounce),
        )
      })
    }
    return this.executeFlowImmediate(flow, executionContext)
  }

  private contextForFlow(flow: Flow, context?: Record<string, unknown>): Record<string, unknown> {
    const base = context ?? {}
    const registeredName = this.registeredFlowNames.get(flow as object)
    const separator = registeredName?.indexOf(".") ?? -1
    if (!registeredName || separator <= 0) return base
    const blockNamespace = registeredName.slice(0, separator)
    if (base[ACTION_FLOW_BLOCK_NAMESPACE_CONTEXT_KEY] === blockNamespace) return base
    return {
      ...base,
      [ACTION_FLOW_BLOCK_NAMESPACE_CONTEXT_KEY]: blockNamespace,
    }
  }

  private resolveExecutionPolicy(
    flow: Flow,
    context: Record<string, unknown>,
    options: ActionFlowExecutionOptions,
  ): ResolvedActionFlowExecutionPolicy {
    const executionId = options.executionId ?? buildExecutionId(flow)
    const timeoutCandidate = options.timeoutMs ?? flow.execution?.timeoutMs
    const timeoutMs = positiveInteger(timeoutCandidate)
    const idempotencyWindowMs =
      positiveInteger(
        options.idempotencyWindowMs ?? flow.execution?.idempotencyWindowMs,
      ) ?? 0
    const rawIdempotencyKey =
      options.idempotencyKey ?? flow.execution?.idempotencyKey
    const resolvedIdempotencyKey = rawIdempotencyKey
      ? this.resolveValue(rawIdempotencyKey, context)
      : undefined
    const duplicatePolicy =
      options.duplicatePolicy ??
      flow.execution?.duplicatePolicy ??
      (rawIdempotencyKey ? "join" : "parallel")
    const flowIdentity =
      this.registeredFlowNames.get(flow as object) ?? flow.name ?? "anonymous"
    const needsExecutionKey =
      duplicatePolicy !== "parallel" || idempotencyWindowMs > 0
    const idempotencyKey =
      resolvedIdempotencyKey == null || String(resolvedIdempotencyKey).length === 0
        ? needsExecutionKey
          ? flowIdentity
          : undefined
        : String(resolvedIdempotencyKey)

    return {
      executionId,
      ...(timeoutMs ? { timeoutMs } : {}),
      duplicatePolicy,
      ...(idempotencyKey ? { idempotencyKey } : {}),
      idempotencyWindowMs,
      ...(idempotencyKey
        ? { executionKey: `${flowIdentity}:${idempotencyKey}` }
        : {}),
    }
  }

  private findDuplicateExecution(
    policy: ResolvedActionFlowExecutionPolicy,
  ): { executionId: string; promise?: Promise<void> } | null {
    if (!policy.executionKey) return null
    const completed = this.completedExecutions.get(policy.executionKey)
    if (completed) {
      if (completed.expiresAt > Date.now()) {
        return { executionId: completed.executionId }
      }
      this.completedExecutions.delete(policy.executionKey)
    }
    if (policy.duplicatePolicy === "parallel") return null
    const active = this.activeExecutions.get(policy.executionKey)
    return active
      ? { executionId: active.executionId, promise: active.promise }
      : null
  }

  private executionControl(
    context: Record<string, unknown>,
  ): ActionFlowExecutionControl | null {
    const candidate = context[ACTION_FLOW_EXECUTION_CONTROL_CONTEXT_KEY]
    if (!candidate || typeof candidate !== "object") return null
    return candidate as ActionFlowExecutionControl
  }

  private throwIfExecutionAborted(context: Record<string, unknown>): void {
    const control = this.executionControl(context)
    if (!control?.signal.aborted) return
    throw executionAbortError(control)
  }

  private raceWithExecutionControl<T>(
    work: Promise<T>,
    context: Record<string, unknown>,
  ): Promise<T> {
    const control = this.executionControl(context)
    if (!control) return work
    if (control.signal.aborted) {
      void work.catch(() => {})
      return Promise.reject(executionAbortError(control))
    }
    return new Promise<T>((resolve, reject) => {
      let settled = false
      const finish = (callback: () => void) => {
        if (settled) return
        settled = true
        control.signal.removeEventListener("abort", onAbort)
        callback()
      }
      const onAbort = () => finish(() => reject(executionAbortError(control)))
      control.signal.addEventListener("abort", onAbort, { once: true })
      work.then(
        (value) => finish(() => resolve(value)),
        (error) => finish(() => reject(error)),
      )
    })
  }

  private cleanupExecutionContext(
    context: Record<string, unknown>,
  ): Record<string, unknown> {
    const cleaned = { ...context }
    delete cleaned[ACTION_FLOW_EXECUTION_CONTROL_CONTEXT_KEY]
    return cleaned
  }

  async executeFlowWithTrace(
    flow: Flow,
    context?: Record<string, unknown>,
    options: ActionFlowExecutionOptions = {},
  ): Promise<ActionFlowExecutionTrace> {
    const executionContext = this.contextForFlow(flow, context)
    const policy = this.resolveExecutionPolicy(flow, executionContext, options)
    const startedAtMs = Date.now()
    const previousTrace = this.activeTrace
    const previousStepIndex = this.traceStepIndex
    const previousParentStack = this.traceParentStack
    const trace: ActionFlowExecutionTrace = {
      contractVersion: ACTION_FLOW_TRACE_CONTRACT_VERSION,
      traceId: buildTraceId(flow),
      executionId: policy.executionId,
      flowName: flow.name,
      trigger: flow.trigger,
      startedAt: new Date(startedAtMs).toISOString(),
      ...(policy.idempotencyKey
        ? { idempotencyKey: policy.idempotencyKey }
        : {}),
      startState: cloneTraceValue(this.state.getSnapshot()) as Record<string, unknown>,
      finalState: {},
      steps: [],
      status: "ok",
    }

    const duplicate = this.findDuplicateExecution(policy)
    if (duplicate) {
      trace.duplicateOfExecutionId = duplicate.executionId
      if (policy.duplicatePolicy === "reject") {
        const error = duplicateExecutionError(policy, duplicate.executionId)
        trace.status = "error"
        trace.error = error.message
        this.finishExecutionTrace(trace, startedAtMs)
        throw Object.assign(error, { actionFlowTrace: trace })
      }
      try {
        if (duplicate.promise) await duplicate.promise
        trace.status = "deduplicated"
        this.finishExecutionTrace(trace, startedAtMs)
        return trace
      } catch (error) {
        trace.status = executionStatusForError(error)
        trace.error = errorToMessage(error)
        this.finishExecutionTrace(trace, startedAtMs)
        throw Object.assign(error instanceof Error ? error : new Error(trace.error), {
          actionFlowTrace: trace,
        })
      }
    }

    this.activeTrace = trace
    this.traceStepIndex = 0
    this.traceParentStack = []
    try {
      await this.executeFlow(flow, executionContext, {
        ...options,
        executionId: policy.executionId,
      })
      trace.steps.sort((a, b) => a.index - b.index)
      this.finishExecutionTrace(trace, startedAtMs)
      return trace
    } catch (error) {
      trace.steps.sort((a, b) => a.index - b.index)
      trace.status = executionStatusForError(error)
      trace.error = errorToMessage(error)
      this.finishExecutionTrace(trace, startedAtMs)
      throw Object.assign(error instanceof Error ? error : new Error(trace.error), { actionFlowTrace: trace })
    } finally {
      this.activeTrace = previousTrace
      this.traceStepIndex = previousStepIndex
      this.traceParentStack = previousParentStack
    }
  }

  private finishExecutionTrace(
    trace: ActionFlowExecutionTrace,
    startedAtMs: number,
  ): void {
    const finishedAtMs = Date.now()
    trace.finishedAt = new Date(finishedAtMs).toISOString()
    trace.durationMs = Math.max(0, finishedAtMs - startedAtMs)
    trace.finalState = cloneTraceValue(this.state.getSnapshot()) as Record<
      string,
      unknown
    >
  }

  clearDebounceTimers(): void {
    for (const timer of this.debounceTimers.values()) {
      clearTimeout(timer)
    }
    this.debounceTimers.clear()
    // Settle pending flow-level debounced calls so awaiters don't hang on teardown.
    for (const resolve of this.flowDebounceResolvers.values()) resolve()
    this.flowDebounceResolvers.clear()
    // Lane B-4 execution-control cleanup (action-level debounce timers + queues).
    for (const timer of this.actionDebounceTimers.values()) {
      clearTimeout(timer)
    }
    this.actionDebounceTimers.clear()
    for (const timer of this.actionDebounceMaxTimers.values()) {
      clearTimeout(timer)
    }
    this.actionDebounceMaxTimers.clear()
    this.actionDebouncePendingTrailing.clear()
    this.actionDebounceLatestRun.clear()
    // Settle any pending debounced calls so awaiters don't hang on teardown.
    for (const resolve of this.actionDebounceResolvers.values()) resolve()
    this.actionDebounceResolvers.clear()
    this.actionQueues.clear()
    this.queueDraining.clear()
    this.throttleTimestamps.clear()
    for (const execution of this.activeExecutions.values()) {
      execution.controller.abort(
        new ActionFlowExecutionError(
          "ACTIONFLOW_CANCELLED",
          execution.executionId,
          `ActionFlow execution "${execution.executionId}" was cancelled during runtime cleanup.`,
        ),
      )
    }
    this.activeExecutions.clear()
    this.completedExecutions.clear()
  }

  registerAction(
    name: string,
    handler: (params: Record<string, unknown>) => unknown | Promise<unknown>,
  ): void {
    const publicHandler = handler as (
      params: Record<string, unknown>,
      context?: Record<string, unknown>,
    ) => unknown | Promise<unknown>
    this.builtinActions[name] = (params, context) =>
      publicHandler(
        params,
        context ? this.cleanupExecutionContext(context) : context,
      )
  }

  // ---- Flow execution ----

  private async executeFlowImmediate(flow: Flow, context?: Record<string, unknown>): Promise<void> {
    try {
      for (const [index, step] of flow.steps.entries()) {
        await this.executeStep(step, context ?? {}, `steps.${index}`)
      }
    } catch (e) {
      if (e && typeof e === "object" && (e as Record<string, unknown>).__stopFlow) {
        if (this.activeTrace) {
          this.activeTrace.status = "stopped"
          const reason = (e as Record<string, unknown>).reason
          if (reason != null) this.activeTrace.stopReason = String(reason)
        }
        return
      }
      throw e
    }
  }

  private async executeStep(step: FlowStep, context: Record<string, unknown>, path?: string): Promise<void> {
    this.throwIfExecutionAborted(context)
    const stepType = step.type || "basic"
    switch (stepType) {
      case "basic":
        await this.executeBasic(step as BasicStep, context, path)
        break
      case "conditional":
        await this.executeConditional(step as ConditionalStep, context, path)
        break
      case "expression":
        await this.executeBasic(step as unknown as BasicStep, context, path)
        break
      case "loop":
        await this.executeLoop(step as LoopStep, context, path)
        break
      case "parallel":
        await this.executeParallel(step as ParallelStep, context, path)
        break
      case "tryCatch":
        await this.executeTryCatch(step as TryCatchStep, context, path)
        break
      case "switch":
        await this.executeSwitch(step as SwitchStep, context, path)
        break
      case "stage":
        await this.executeStage(step as StageStep, context, path)
        break
      case "saga":
        await this.executeSaga(step as SagaStep, context, path)
        break
      default:
        if (this.isSourceTypedActionStep(stepType)) {
          await this.executeBasic(this.sourceTypedStepToBasic(step, stepType), context, path)
          break
        }
        console.warn(`[runtime-v2] Unknown step type: ${stepType}`)
        break
    }
  }

  private isSourceTypedActionStep(stepType: string): boolean {
    return Boolean(this.resolveBuiltin(stepType))
  }

  private sourceTypedStepToBasic(step: FlowStep, action: string): BasicStep {
    const record = step as unknown as Record<string, unknown>
    const params = isPlainTraceObject(record.params) ? { ...(record.params as Record<string, unknown>) } : {}
    for (const [key, value] of Object.entries(record)) {
      if (key === "type" || key === "action" || key === "name" || key === "params") continue
      params[key] = value
    }
    if (action === "callback" && params.callback == null && params.sourceExpression != null) {
      params.callback = params.sourceExpression
    }
    return {
      type: "basic",
      ...(typeof record.name === "string" ? { name: record.name } : {}),
      action,
      params,
    }
  }

  private async executeBasic(step: BasicStep, context: Record<string, unknown>, path?: string): Promise<void> {
    const rawParams = step.params
    const resolvedParams = this.resolveParams(rawParams, context)
    const trace = this.startStepTrace({
      type: step.type || "basic",
      ...(step.name ? { name: step.name } : {}),
      action: step.action,
      params: resolvedParams,
      path,
    })

    // Restore raw #{item.*} refs — these are resolved per-item in StateManager.
    // But pre-resolve #{event.*} refs since StateManager doesn't have event context.
    if (step.action === "setState" && rawParams.operation === "upsert" && rawParams.onMatch) {
      resolvedParams.onMatch = this.resolveEventRefsInObject(rawParams.onMatch as Record<string, unknown>, context)
    }
    if (step.action === "setState" && rawParams.operation === "updateWhere" && rawParams.update) {
      resolvedParams.update = this.resolveEventRefsInObject(rawParams.update as Record<string, unknown>, context)
    }
    // Same handling for standalone action aliases (upsertWhere)
    // onMatch contains #{item.*} refs that must stay raw for per-item evaluation
    if (step.action === "upsertWhere" && rawParams.onMatch) {
      resolvedParams.onMatch = this.resolveEventRefsInObject(rawParams.onMatch as Record<string, unknown>, context)
    }
    // For standalone updateWhere, updates are fully resolved by resolveParams
    // (no #{item.*} refs to preserve), so no override needed

    // Restore raw expression for compute action
    if (step.action === "compute" && rawParams.expression) {
      let expr = String(rawParams.expression)
      if (expr.startsWith("#{") && expr.endsWith("}")) {
        expr = expr.slice(2, -1)
      }
      resolvedParams.expression = this.resolveEventRefs(expr, context)
    }

    // Meta-actions re-dispatch their sub-actions through executeBasic, so keep the
    // sub-action-bearing fields RAW here (avoids pre-resolving nested setState
    // #{item.*} onMatch/update before per-item evaluation — Lane B-4).
    if (META_SUBACTION_ACTIONS.has(step.action)) {
      // `action` covers the object-shaped single sub-action { action, params }
      // (TBox debounce/retry/throttle) so its nested params stay raw too.
      for (const f of ["actions", "steps", "rollback", "params", "action"] as const) {
        if (rawParams[f] !== undefined) resolvedParams[f] = rawParams[f]
      }
    }

    const action = this.builtinActions[ACTION_NAME_ALIASES[step.action] ?? step.action]
    if (action) {
      try {
        const runAction = () => action(resolvedParams, context)
        const runControlledAction = () => {
          this.throwIfExecutionAborted(context)
          return this.raceWithExecutionControl(
            Promise.resolve(runAction()),
            context,
          )
        }
        const result = step.action === "callFlow" && trace
          ? await this.withAsyncTraceParent(trace, runControlledAction)
          : await runControlledAction()
        // Auto-capture handler return value at _results.<step.name>.
        // Opt-in: only when handler returns non-undefined AND step.name is set.
        if (result !== undefined && step.name) {
          this.state.setState(`_results.${step.name}`, result)
        }
        this.finishStepTrace(trace, "ok", result)
      } catch (error) {
        this.finishStepTrace(trace, "error", undefined, errorToMessage(error))
        throw error
      }
    } else {
      console.warn(`[runtime-v2] Unknown action: ${step.action}`)
      this.finishStepTrace(trace, "skipped")
    }
  }

  private startStepTrace(input: {
    type: string
    name?: string
    action?: string
    params?: Record<string, unknown>
    detail?: Record<string, unknown>
    path?: string
  }): { entry: ActionFlowStepTrace; before: Record<string, unknown> } | null {
    if (!this.activeTrace) return null
    const parentIndex = this.traceParentStack[this.traceParentStack.length - 1]
    return {
      entry: {
        index: this.traceStepIndex++,
        ...(parentIndex !== undefined ? { parentIndex } : {}),
        ...(input.path ? { path: input.path } : {}),
        type: input.type,
        ...(input.name ? { name: input.name } : {}),
        ...(input.action ? { action: input.action } : {}),
        ...(input.params ? { params: cloneTraceValue(input.params) as Record<string, unknown> } : {}),
        ...(input.detail ? { detail: cloneTraceValue(input.detail) as Record<string, unknown> } : {}),
        status: "ok",
        stateChanges: [],
      },
      before: cloneTraceValue(this.state.getSnapshot()) as Record<string, unknown>,
    }
  }

  private finishStepTrace(
    trace: { entry: ActionFlowStepTrace; before: Record<string, unknown> } | null,
    status: ActionFlowStepTrace["status"],
    result?: unknown,
    error?: string,
  ): void {
    if (!this.activeTrace || !trace) return
    const after = cloneTraceValue(this.state.getSnapshot()) as Record<string, unknown>
    trace.entry.status = status
    trace.entry.stateChanges = diffTraceState(trace.before, after)
    if (result !== undefined) trace.entry.result = cloneTraceValue(result)
    if (error) trace.entry.error = error
    this.activeTrace.steps.push(trace.entry)
  }

  private async executeConditional(step: ConditionalStep, context: Record<string, unknown>, path?: string): Promise<void> {
    const resolvedCondition = String(this.resolveValue(step.condition, context) ?? step.condition)
    const result = this.compute.evaluate(resolvedCondition)
    const branchName = isTruthy(result) ? "then" : "else"
    const branch = branchName === "then" ? step.then : (step.else ?? [])
    const trace = this.startStepTrace({
      type: "conditional",
      path,
      detail: {
        condition: step.condition,
        resolvedCondition,
        result,
        branch: branchName,
        skippedBranch: branchName === "then" ? "else" : "then",
      },
    })
    try {
      for (const [index, s] of branch.entries()) {
        await this.executeStepWithParent(s, context, trace, path ? `${path}.${branchName}.${index}` : undefined)
      }
      this.finishStepTrace(trace, "ok")
    } catch (error) {
      this.finishStepTrace(trace, "error", undefined, errorToMessage(error))
      throw error
    }
  }

  private async executeLoop(step: LoopStep, context: Record<string, unknown>, path?: string): Promise<void> {
    const collection = this.resolveValue(step.collection, context)
    const trace = this.startStepTrace({
      type: "loop",
      path,
      detail: {
        collection: step.collection,
        itemVar: step.itemVar,
        itemCount: Array.isArray(collection) ? collection.length : 0,
        skipped: !Array.isArray(collection),
      },
    })
    try {
      if (Array.isArray(collection)) {
        for (const [iteration, item] of collection.entries()) {
          const loopCtx = { ...context, [step.itemVar]: item, __iteration: iteration }
          // Every iteration repeats the same syntactic body paths — the
          // path identifies the step DEFINITION, not the execution.
          for (const [index, s] of step.steps.entries()) {
            await this.executeStepWithParent(s, loopCtx, trace, path ? `${path}.steps.${index}` : undefined)
          }
        }
      }
      this.finishStepTrace(trace, "ok")
    } catch (error) {
      this.finishStepTrace(trace, "error", undefined, errorToMessage(error))
      throw error
    }
  }

  private async executeParallel(step: ParallelStep, context: Record<string, unknown>, path?: string): Promise<void> {
    const trace = this.startStepTrace({
      type: "parallel",
      path,
      detail: { stepCount: step.steps.length },
    })
    try {
      await Promise.all(step.steps.map((s, index) =>
        this.executeStepWithParent(s, context, trace, path ? `${path}.steps.${index}` : undefined),
      ))
      this.finishStepTrace(trace, "ok")
    } catch (error) {
      this.finishStepTrace(trace, "error", undefined, errorToMessage(error))
      throw error
    }
  }

  private async executeTryCatch(step: TryCatchStep, context: Record<string, unknown>, path?: string): Promise<void> {
    const trace = this.startStepTrace({
      type: "tryCatch",
      path,
      detail: { branch: "try" },
    })
    let pendingError: unknown
    let hasPendingError = false
    try {
      for (const [index, s] of step.try.entries()) {
        await this.executeStepWithParent(s, context, trace, path ? `${path}.try.${index}` : undefined)
      }
    } catch (e) {
      if (step.catch) {
        if (trace) trace.entry.detail = { ...(trace.entry.detail ?? {}), branch: "catch", error: errorToMessage(e) }
        const errorContext = {
          ...(isExecutionControlError(e)
            ? this.cleanupExecutionContext(context)
            : context),
          error: { message: errorToMessage(e) },
        }
        try {
          for (const [index, s] of step.catch.entries()) {
            await this.executeStepWithParent(s, errorContext, trace, path ? `${path}.catch.${index}` : undefined)
          }
          if (isExecutionControlError(e)) {
            pendingError = e
            hasPendingError = true
          }
        } catch (catchError) {
          pendingError = catchError
          hasPendingError = true
        }
      } else {
        pendingError = e
        hasPendingError = true
      }
    } finally {
      if (step.finally) {
        if (trace) trace.entry.detail = { ...(trace.entry.detail ?? {}), finally: true }
        try {
          const finallyContext = isExecutionControlError(pendingError)
            ? this.cleanupExecutionContext(context)
            : context
          for (const [index, s] of step.finally.entries()) {
            await this.executeStepWithParent(s, finallyContext, trace, path ? `${path}.finally.${index}` : undefined)
          }
        } catch (finallyError) {
          // Match JavaScript try/finally semantics: cleanup failure overrides the
          // prior try/catch failure because it is the last observable failure.
          pendingError = finallyError
          hasPendingError = true
        }
      }
    }

    if (hasPendingError) {
      this.finishStepTrace(trace, "error", undefined, errorToMessage(pendingError))
      throw pendingError
    }
    this.finishStepTrace(trace, "ok")
  }

  private async executeSwitch(step: SwitchStep, context: Record<string, unknown>, path?: string): Promise<void> {
    const resolved = String(this.resolveValue(step.value, context) ?? "")
    const branch = step.cases[resolved] ?? step.default ?? []
    // Mirror the static evidence path segments: `cases.<name>` / `default`.
    const branchSegment = step.cases[resolved] ? `cases.${resolved}` : "default"
    const trace = this.startStepTrace({
      type: "switch",
      path,
      detail: {
        value: step.value,
        resolved,
        branch: step.cases[resolved] ? resolved : "default",
      },
    })
    try {
      for (const [index, s] of branch.entries()) {
        await this.executeStepWithParent(s, context, trace, path ? `${path}.${branchSegment}.${index}` : undefined)
      }
      this.finishStepTrace(trace, "ok")
    } catch (error) {
      this.finishStepTrace(trace, "error", undefined, errorToMessage(error))
      throw error
    }
  }

  /**
   * Execute a named lifecycle boundary in deterministic order:
   * onEnter → steps → onSuccess → onFinally, with onError inserted after a
   * forward failure. A stage is not an ACID transaction; its compensation is
   * declarative until an enclosing saga invokes it.
   */
  private async executeStage(step: StageStep, context: Record<string, unknown>, path?: string): Promise<StageExecutionResult> {
    const failurePolicy = step.failurePolicy ?? "stop"
    const trace = this.startStepTrace({
      type: "stage",
      name: step.name,
      path,
      detail: {
        phase: "onEnter",
        failurePolicy,
        compensationDefined: Boolean(step.compensate?.length),
      },
    })
    let pendingError: unknown
    let hasPendingError = false
    let hookFailed = false
    let stopSignal = false
    let executionControlSignal = false

    const runBranch = async (
      branch: FlowStep[] | undefined,
      phase: string,
      branchContext: Record<string, unknown> = context,
    ): Promise<void> => {
      if (!branch) return
      if (trace) trace.entry.detail = { ...(trace.entry.detail ?? {}), phase }
      const phaseContext = this.withStageEventContext(branchContext, step.name, phase, hasPendingError ? pendingError : undefined)
      for (const [index, child] of branch.entries()) {
        await this.executeStepWithParent(child, phaseContext, trace, path ? `${path}.${phase}.${index}` : undefined)
      }
    }

    try {
      await runBranch(step.onEnter, "onEnter")
      await runBranch(step.steps, "steps")
      await runBranch(step.onSuccess, "onSuccess")
    } catch (error) {
      pendingError = error
      hasPendingError = true
      stopSignal = isStopFlowSignal(error)
      executionControlSignal = isExecutionControlError(error)
      if (!stopSignal && step.onError?.length) {
        try {
          await runBranch(
            step.onError,
            "onError",
            executionControlSignal
              ? this.cleanupExecutionContext(context)
              : context,
          )
        } catch (onErrorFailure) {
          pendingError = onErrorFailure
          hookFailed = true
          stopSignal = isStopFlowSignal(onErrorFailure)
          executionControlSignal = isExecutionControlError(onErrorFailure)
        }
      }
    } finally {
      try {
        await runBranch(
          step.onFinally,
          "onFinally",
          executionControlSignal
            ? this.cleanupExecutionContext(context)
            : context,
        )
      } catch (onFinallyFailure) {
        pendingError = onFinallyFailure
        hasPendingError = true
        hookFailed = true
        stopSignal = isStopFlowSignal(onFinallyFailure)
        executionControlSignal = isExecutionControlError(onFinallyFailure)
      }
    }

    if (hasPendingError) {
      if (
        !stopSignal &&
        !executionControlSignal &&
        failurePolicy === "continue" &&
        step.onError?.length &&
        !hookFailed
      ) {
        if (trace) trace.entry.detail = { ...(trace.entry.detail ?? {}), phase: "handled", handledFailure: true }
        this.finishStepTrace(trace, "ok")
        return { completed: false, trace }
      }
      if (trace) trace.entry.detail = { ...(trace.entry.detail ?? {}), phase: "failed" }
      this.finishStepTrace(trace, "error", undefined, errorToMessage(pendingError))
      throw pendingError
    }

    if (trace) trace.entry.detail = { ...(trace.entry.detail ?? {}), phase: "completed" }
    this.finishStepTrace(trace, "ok")
    return { completed: true, trace }
  }

  /**
   * Execute stages in order and, on failure, compensate previously completed
   * stages in reverse order. The original forward failure stays observable.
   */
  private async executeSaga(step: SagaStep, context: Record<string, unknown>, path?: string): Promise<void> {
    const compensationFailurePolicy = step.compensationFailurePolicy ?? "continue"
    const trace = this.startStepTrace({
      type: "saga",
      path,
      detail: {
        stageCount: step.stages.length,
        compensationFailurePolicy,
      },
    })
    const completed: Array<{ stage: StageStep; index: number; trace: StepTraceHandle }> = []
    let completedStageCount = 0

    try {
      for (const [index, stage] of step.stages.entries()) {
        const result = await this.executeStageWithParent(
          stage,
          context,
          trace,
          path ? `${path}.stages.${index}` : undefined,
        )
        if (result.completed) {
          completedStageCount++
          if (stage.compensate?.length) completed.push({ stage, index, trace: result.trace })
        }
      }
      if (trace) trace.entry.detail = {
        ...(trace.entry.detail ?? {}),
        completedStages: completedStageCount,
        phase: "completed",
      }
      this.finishStepTrace(trace, "ok")
    } catch (forwardFailure) {
      if (isStopFlowSignal(forwardFailure)) {
        if (trace) trace.entry.detail = {
          ...(trace.entry.detail ?? {}),
          phase: "stopped",
          completedStages: completedStageCount,
          compensationsAttempted: 0,
        }
        this.finishStepTrace(trace, "error", undefined, errorToMessage(forwardFailure))
        throw forwardFailure
      }

      const compensationErrors: string[] = []
      let compensationsAttempted = 0

      for (const completedStage of [...completed].reverse()) {
        const compensationBaseContext = isExecutionControlError(forwardFailure)
          ? this.cleanupExecutionContext(context)
          : context
        const compensationContext = this.withStageEventContext(
          compensationBaseContext,
          completedStage.stage.name,
          "compensate",
          forwardFailure,
        )
        compensationsAttempted++
        try {
          for (const [index, child] of completedStage.stage.compensate!.entries()) {
            await this.executeStepWithParent(
              child,
              compensationContext,
              completedStage.trace ?? trace,
              path ? `${path}.stages.${completedStage.index}.compensate.${index}` : undefined,
            )
          }
        } catch (compensationFailure) {
          compensationErrors.push(
            `${completedStage.stage.name}: ${errorToMessage(compensationFailure)}`,
          )
          if (compensationFailurePolicy === "stop") break
        }
      }

      if (trace) trace.entry.detail = {
        ...(trace.entry.detail ?? {}),
        phase: "compensated",
        completedStages: completedStageCount,
        compensationsAttempted,
        compensationErrors,
      }
      this.finishStepTrace(trace, "error", undefined, errorToMessage(forwardFailure))

      const observableError = forwardFailure instanceof Error
        ? forwardFailure
        : new Error(errorToMessage(forwardFailure))
      if (compensationErrors.length > 0) {
        Object.assign(observableError, { compensationErrors })
      }
      throw observableError
    }
  }

  private async executeStageWithParent(
    stage: StageStep,
    context: Record<string, unknown>,
    parentTrace: StepTraceHandle,
    path?: string,
  ): Promise<StageExecutionResult> {
    if (!parentTrace) return this.executeStage(stage, context, path)
    this.traceParentStack.push(parentTrace.entry.index)
    try {
      return await this.executeStage(stage, context, path)
    } finally {
      this.traceParentStack.pop()
    }
  }

  private withStageEventContext(
    context: Record<string, unknown>,
    stageName: string,
    phase: string,
    error?: unknown,
  ): Record<string, unknown> {
    const event = isPlainTraceObject(context.event)
      ? context.event as Record<string, unknown>
      : {}
    return {
      ...context,
      event: {
        ...event,
        actionFlow: {
          stage: stageName,
          phase,
          ...(error !== undefined ? { error: errorToMessage(error) } : {}),
        },
      },
    }
  }

  private async executeStepWithParent(
    step: FlowStep,
    context: Record<string, unknown>,
    parentTrace: StepTraceHandle,
    path?: string,
  ): Promise<void> {
    if (!parentTrace) {
      await this.executeStep(step, context, path)
      return
    }
    this.traceParentStack.push(parentTrace.entry.index)
    try {
      await this.executeStep(step, context, path)
    } finally {
      this.traceParentStack.pop()
    }
  }

  private async withAsyncTraceParent<T>(
    trace: StepTraceHandle,
    callback: () => T | Promise<T>,
  ): Promise<T> {
    if (!trace) return callback()
    this.traceParentStack.push(trace.entry.index)
    try {
      return await callback()
    } finally {
      this.traceParentStack.pop()
    }
  }

  // ---- Param Resolution ----

  private resolveParams(params: Record<string, unknown>, ctx: Record<string, unknown>): Record<string, unknown> {
    const result: Record<string, unknown> = {}
    for (const [key, value] of Object.entries(params)) {
      result[key] = this.resolveValue(value, ctx)
    }
    return result
  }

  private resolveValue(value: unknown, ctx: Record<string, unknown>): unknown {
    if (typeof value === "string") {
      const localMatch = /^@\{([^}]+)\}$/.exec(value)
      if (localMatch) {
        const inner = localMatch[1].trim()
        if (inner.startsWith("!")) return !this.state.get(inner.slice(1))
        return this.state.get(inner)
      }
      if (value.includes("@{") && !value.includes("#{")) {
        return value.replace(/@\{([^}]+)\}/g, (_, inner) => {
          const path = (inner as string).trim()
          const val = this.state.get(path)
          return val != null ? String(val) : ""
        })
      }
      const pureMatch = /^#\{([^}]+)\}$/.exec(value)
      if (pureMatch) {
        const inner = pureMatch[1].trim()
        const ctxVal = this.getFromContext(inner, ctx)
        if (ctxVal !== undefined) return ctxVal
        const stateVal = this.state.get(inner)
        if (stateVal !== undefined) return stateVal
        if (/[+\-*/<>=?!|&(]/.test(inner)) {
          return this.compute.evaluate(this.resolveEventRefs(inner, ctx))
        }
        return undefined
      }
      if (value.includes("#{")) {
        let result: string = value.replace(/#\{([^}]+)\}/g, (_, ref) => {
          const r = (ref as string).trim()
          const ctxVal = this.getFromContext(r, ctx)
          let resolved = ctxVal !== undefined ? ctxVal : this.state.get(r)
          if (resolved == null && /[+\-*/<>=?!|&(]/.test(r)) {
            resolved = this.compute.evaluate(this.resolveEventRefs(r, ctx))
          }
          if (resolved == null) return ""
          return String(resolved)
        })
        if (result.includes("@{")) {
          result = result.replace(/@\{([^}]+)\}/g, (_, inner) => {
            const path = (inner as string).trim()
            const val = this.state.get(path)
            return val != null ? String(val) : ""
          })
        }
        return result
      }
      return value
    }
    if (Array.isArray(value)) {
      return value.map((v) => this.resolveValue(v, ctx))
    }
    if (value !== null && typeof value === "object") {
      return this.resolveParams(value as Record<string, unknown>, ctx)
    }
    return value
  }

  /**
   * Resolve #{event.*} refs in onMatch/update objects while preserving #{item.*} refs.
   * This ensures event context is available for per-item expressions like #{item.quantity + event.quantity}.
   */
  private resolveEventRefsInObject(obj: Record<string, unknown>, ctx: Record<string, unknown>): Record<string, unknown> {
    const result: Record<string, unknown> = {}
    for (const [key, val] of Object.entries(obj)) {
      if (typeof val === "string" && val.includes("#{")) {
        // Replace #{event.xxx} with actual values, keep #{item.xxx} as-is
        result[key] = val.replace(/#\{([^}]+)\}/g, (full, inner) => {
          const expr = (inner as string).trim()
          // Pure event ref: #{event.quantity} → resolve completely
          if (/^event\.\w+$/.test(expr)) {
            const resolved = this.getFromContext(expr, ctx)
            if (resolved !== undefined) return `#{${resolved}}`  // wrap as literal for StateManager
            return full
          }
          // Mixed: #{item.quantity + event.quantity} → resolve event refs inline
          if (expr.includes("event.")) {
            const resolved = this.resolveEventRefs(expr, ctx)
            return `#{${resolved}}`
          }
          return full  // pure item refs: keep as-is
        })
      } else {
        result[key] = val
      }
    }
    return result
  }

  private resolveEventRefs(expr: string, ctx: Record<string, unknown>): string {
    const event = ctx.event
    if (event == null || typeof event !== "object") return expr
    return expr.replace(/\bevent\.([a-zA-Z_]\w*(?:\.[a-zA-Z_]\w*)*)/g, (match, dottedPath: string) => {
      const segments = dottedPath.split(".")
      // Resolve the longest context path while preserving a trailing accessor,
      // e.g. event.user.name.toUpperCase() -> "Ada".toUpperCase(). This also
      // makes nested lifecycle refs such as event.actionFlow.phase usable by
      // compute expressions instead of producing "[object Object].phase".
      for (let length = segments.length; length > 0; length--) {
        const resolvedPath = segments.slice(0, length).join(".")
        const value = this.getFromContext(`event.${resolvedPath}`, ctx)
        if (value === undefined) continue
        const suffix = segments.slice(length).map((segment) => `.${segment}`).join("")
        if (typeof value === "string") return `${JSON.stringify(value)}${suffix}`
        if (value === null) return `null${suffix}`
        if (typeof value === "object") {
          const serialized = JSON.stringify(value)
          return serialized === undefined ? match : `${serialized}${suffix}`
        }
        return `${String(value)}${suffix}`
      }
      return match
    })
  }

  private getFromContext(path: string, ctx: Record<string, unknown>): unknown {
    const keys = path.split(".")
    let current: unknown = ctx
    for (const key of keys) {
      if (current == null || typeof current !== "object") return undefined
      current = (current as Record<string, unknown>)[key]
    }
    return current
  }

  // ---- Built-in Actions (strict v2 — no aliases) ----

  /** setState — strict "target" only (no "path" alias). */
  private actionSetState(params: Record<string, unknown>): void {
    const { operation, value } = params as {
      target: string
      operation?: string
      value?: unknown
    }
    if (typeof params.target !== "string" || !params.target) {
      console.warn("[runtime-v2] setState: missing target")
      return
    }
    const target = params.target.replace(/^#/, "")

    switch (operation) {
      case "append":
        this.state.appendTo(target, value)
        break
      case "upsert": {
        const match = params.match as string | undefined
        const onMatch = params.onMatch as Record<string, unknown> | undefined
        const onNew = params.onNew as Record<string, unknown> | undefined
        this.state.upsertWhere(target, match, onMatch, onNew)
        break
      }
      case "removeWhere": {
        const predicate = params.predicate as string | undefined
        if (predicate) {
          this.state.removeWhere(target, (item) =>
            this.compute.evalPredicate(predicate, item),
          )
        }
        break
      }
      case "updateWhere": {
        const pred = params.predicate as string | undefined
        const update = params.update as Record<string, unknown> | undefined
        if (pred && update) {
          this.state.updateWhere(
            target,
            (item) => this.compute.evalPredicate(pred, item),
            update,
          )
        }
        break
      }
      default:
        if (
          value !== null &&
          typeof value === "object" &&
          !Array.isArray(value)
        ) {
          const current = this.state.get(target)
          if (
            current !== null &&
            typeof current === "object" &&
            !Array.isArray(current)
          ) {
            this.state.setState(target, { ...(current as Record<string, unknown>), ...(value as Record<string, unknown>) })
            return
          }
        }
        this.state.setState(target, value)
    }
  }

  private actionSortByField(params: Record<string, unknown>): void {
    const source = params.source as string | undefined
    const target = params.target as string | undefined
    const field = params.field as string | undefined
    const direction = (params.direction as string | undefined) ?? "asc"
    if (!source || !target || !field) return

    const rows = this.state.get(source.replace(/^#/, ""))
    if (!Array.isArray(rows)) return

    const sorted = [...rows].sort((a, b) => {
      const av = readField(a, field)
      const bv = readField(b, field)
      if (av == null && bv == null) return 0
      if (av == null) return 1
      if (bv == null) return -1
      if (typeof av === "number" && typeof bv === "number") {
        return direction === "desc" ? bv - av : av - bv
      }
      const as = String(av).toLowerCase()
      const bs = String(bv).toLowerCase()
      if (as < bs) return direction === "desc" ? 1 : -1
      if (as > bs) return direction === "desc" ? -1 : 1
      return 0
    })

    this.state.setState(target.replace(/^#/, ""), sorted)
  }

  private actionComputeDateRange(params: Record<string, unknown>): void {
    const range = String(params.range ?? "")
    const now = new Date()
    const today = now.toISOString().split("T")[0] ?? ""
    let from = today

    if (range === "yesterday") {
      const d = new Date(now)
      d.setDate(d.getDate() - 1)
      from = d.toISOString().split("T")[0] ?? today
    } else if (range === "7days") {
      const d = new Date(now)
      d.setDate(d.getDate() - 7)
      from = d.toISOString().split("T")[0] ?? today
    } else if (range === "15days") {
      const d = new Date(now)
      d.setDate(d.getDate() - 15)
      from = d.toISOString().split("T")[0] ?? today
    } else if (range === "1month") {
      const d = new Date(now)
      d.setMonth(d.getMonth() - 1)
      from = d.toISOString().split("T")[0] ?? today
    } else if (range === "3months") {
      const d = new Date(now)
      d.setMonth(d.getMonth() - 3)
      from = d.toISOString().split("T")[0] ?? today
    }

    const prefix = String(params.statePrefix ?? "ui")
    const dot = prefix ? `${prefix}.` : ""
    this.state.setState(`${dot}dateFrom`, from)
    this.state.setState(`${dot}dateTo`, today)
    this.state.setState(`${dot}dateFromCompact`, from.replace(/-/g, ""))
    this.state.setState(`${dot}dateToCompact`, today.replace(/-/g, ""))
    this.state.setState(`${dot}dateRange`, range)
    this.state.setState(`${dot}pageIndex`, 0)
  }

  // ── Data / table mutation actions ──
  // Ported from v1 @cogo/runtime-core/action-flow-engine.ts. They use the same
  // this.state (get/setState) + this.compute surface as v2's existing actions and
  // reify in actionflow-executors-v1.ttl so the execution-drift gate clears them.

  /** Group an array by keyField (optional substring slice), summing sumFields. */
  private actionGroupBy(params: Record<string, unknown>): void {
    const sourcePath = String(params.source ?? "").replace(/^#/, "")
    const keyField = String(params.keyField ?? "")
    const keySlice = params.keySlice != null ? Number(params.keySlice) : 0
    const sumFields = Array.isArray(params.sumFields) ? params.sumFields.map(String) : []
    const targetPath = String(params.target ?? "").replace(/^#/, "")
    if (!sourcePath || !keyField || !targetPath) return

    const source = this.state.get(sourcePath)
    if (!Array.isArray(source)) return

    const groupMap = new Map<string, Record<string, unknown>>()
    for (const item of source) {
      const row = item as Record<string, unknown>
      let key = String(row[keyField] ?? "")
      if (keySlice > 0) key = key.substring(0, keySlice)
      if (!groupMap.has(key)) groupMap.set(key, { [keyField]: key })
      const group = groupMap.get(key)!
      for (const field of sumFields) {
        group[field] = Number(group[field] ?? 0) + Number(row[field] ?? 0)
      }
    }
    this.state.setState(targetPath, Array.from(groupMap.values()))
  }

  /**
   * Add a computed field to each item in a source array. The `expression`
   * references bare `item.<key>`; literal values are substituted then evaluated
   * (the ComputeEvaluator has no per-item value scope). Defaults target=source.
   */
  private actionComputeField(params: Record<string, unknown>): void {
    const sourcePath = String(params.source ?? "").replace(/^#/, "")
    const field = String(params.field ?? "")
    const expression = String(params.expression ?? "")
    const targetPath = String(params.target ?? params.source ?? "").replace(/^#/, "")
    if (!sourcePath || !field || !expression) return

    const source = this.state.get(sourcePath)
    if (!Array.isArray(source)) return

    const result = source.map((row) => {
      const item = row as Record<string, unknown>
      const resolved = expression.replace(/\bitem\.(\w+)/g, (_m, key) => {
        const val = item[key as string]
        if (val === undefined || val === null) return "0"
        if (typeof val === "number") return String(val)
        if (typeof val === "string") {
          const n = parseFloat(val)
          return isNaN(n) ? `'${val}'` : String(n)
        }
        return String(val)
      })
      try {
        return { ...item, [field]: this.compute.evaluate(resolved) }
      } catch {
        return { ...item, [field]: null }
      }
    })
    this.state.setState(targetPath, result)
  }

  /** Set a single cell in a table array: array[rowIndex][field] = value. */
  private actionSetTableCell(params: Record<string, unknown>): void {
    const target = String(params.target ?? "").replace(/^#/, "")
    const rowIndex = Number(params.rowIndex ?? -1)
    const field = String(params.field ?? "")
    if (!target || rowIndex < 0 || !field) return

    const arr = this.state.get(target)
    if (!Array.isArray(arr) || rowIndex >= arr.length) return

    const updated = [...arr]
    updated[rowIndex] = { ...(updated[rowIndex] as Record<string, unknown>), [field]: params.value }
    this.state.setState(target, updated)
  }

  /** Add a row to a table array (position "start" | "end", default "start"). */
  private actionAddTableRow(params: Record<string, unknown>): void {
    const target = String(params.target ?? "").replace(/^#/, "")
    if (!target) return

    const arr = this.state.get(target)
    if (!Array.isArray(arr)) return

    const row = (params.row as Record<string, unknown>) ?? {}
    const position = String(params.position ?? "start")
    this.state.setState(target, position === "end" ? [...arr, row] : [row, ...arr])
  }

  /** Delete a row from a table array by index. */
  private actionDeleteTableRow(params: Record<string, unknown>): void {
    const target = String(params.target ?? "").replace(/^#/, "")
    const rowIndex = Number(params.rowIndex ?? -1)
    if (!target || rowIndex < 0) return

    const arr = this.state.get(target)
    if (!Array.isArray(arr) || rowIndex >= arr.length) return

    this.state.setState(target, arr.filter((_, i) => i !== rowIndex))
  }

  /**
   * Display a confirmation dialog and return { confirmed }. The result is
   * auto-captured at _results.<step.name> for downstream conditional branching
   * (cf. the TBox cevent:confirm / cevent:cancel events). Fail-closed: with no
   * platform confirm capability the action does NOT proceed (confirmed=false).
   */
  private async actionConfirmAction(
    params: Record<string, unknown>,
  ): Promise<{ confirmed: boolean }> {
    const platform = this.platform
    if (!platform || !platform.confirm) return { confirmed: false }
    const confirmed = await platform.confirm({
      message: String(params.message ?? ""),
      ...(params.title != null ? { title: String(params.title) } : {}),
      ...(params.confirmLabel != null ? { confirmLabel: String(params.confirmLabel) } : {}),
      ...(params.cancelLabel != null ? { cancelLabel: String(params.cancelLabel) } : {}),
      ...(params.dangerous != null ? { dangerous: Boolean(params.dangerous) } : {}),
    })
    return { confirmed: Boolean(confirmed) }
  }

  private actionNavigate(params: Record<string, unknown>): void {
    if (!this.platform) return
    const mode = (params.mode as string) ?? "push"
    // Accept `to` (the C7-migrator / newer navigate contract — what flow-simulator.ts
    // and the verified-flows corpus author) alongside `route`/`target`. Matches the
    // simulator's `to ?? route` precedence so simulation and runtime agree — closes
    // the sim↔runtime divergence that left ~35 `to`-authored navigate steps a silent
    // no-op at runtime (cogo-web #746 / Wave 12.b).
    let route = (params.to ?? params.route ?? params.target) as string | undefined
    // Merge a `params` object into the route as a query string (e.g. {id, tab} →
    // "?id=…&tab=…"). Read into a local so the static executor-param parser does
    // not see `Object.entries(params…)` as a whole-`params` iteration (which would
    // mark the executor opaque and drop it from the analyzable surface).
    const query = params.params as Record<string, unknown> | undefined
    if (route && query && typeof query === "object") {
      const qs = new URLSearchParams()
      for (const [k, v] of Object.entries(query)) if (v != null) qs.set(k, String(v))
      const q = qs.toString()
      if (q) route += (route.includes("?") ? "&" : "?") + q
    }
    // Optional named transition/animation forwarded to the platform router.
    // Only pass the options arg WHEN a transition is set, so the common case stays
    // a clean single-arg call (no trailing-undefined that would break callers'
    // exact-arg assertions).
    const transition = params.transition as string | undefined
    switch (mode) {
      case "replace":
        if (route) transition ? this.platform.replaceRoute(route, { transition }) : this.platform.replaceRoute(route)
        break
      case "push":
      default:
        if (route) transition ? this.platform.pushRoute(route, { transition }) : this.platform.pushRoute(route)
        break
    }
  }

  private actionShowToast(params: Record<string, unknown>): void {
    if (this.onToast) {
      this.onToast({
        message: String(params.message ?? ""),
        description: params.description ? String(params.description) : undefined,
        variant: params.variant ? String(params.variant) : undefined,
        // Optional auto-dismiss duration (ms) forwarded to the host toast sink.
        durationMs: params.durationMs != null ? Number(params.durationMs) : undefined,
      })
    }
  }

  private actionDelay(
    params: Record<string, unknown>,
    context?: Record<string, unknown>,
  ): Promise<void> {
    const raw = Number(params.duration ?? params.ms ?? 0)
    // Optional time unit: "s" → ×1000, "m" → ×60000, default "ms" → ×1.
    // Back-compat: omitting `unit` leaves the value in milliseconds.
    const unit = String(params.unit ?? "ms")
    const factor = unit === "s" ? 1000 : unit === "m" ? 60000 : 1
    const control = context ? this.executionControl(context) : null
    if (control?.signal.aborted) return Promise.reject(executionAbortError(control))
    return new Promise((resolve, reject) => {
      const timer = setTimeout(() => {
        control?.signal.removeEventListener("abort", onAbort)
        resolve()
      }, raw * factor)
      const onAbort = () => {
        clearTimeout(timer)
        reject(executionAbortError(control!))
      }
      control?.signal.addEventListener("abort", onAbort, { once: true })
    })
  }

  private async actionRestApi(
    params: Record<string, unknown>,
    context?: Record<string, unknown>,
  ): Promise<void> {
    // `endpoint` is a wild legacy restful_data_action spelling of url
    // (scripts/reconcile-corpus-actions.mjs tier-1 rule; same read-tolerant
    // idiom as actionGraphql's url ?? endpoint).
    const url = String(params.url ?? params.endpoint ?? "")
    // No-op without a url (same missing-required-param idiom as
    // actionLoadDatasource) — keeps an alias dispatch with empty params safe
    // and never resolves a bare fetch("") against the document base.
    if (!url) {
      console.warn("[ActionFlow] restApi: missing url/endpoint — step no-ops")
      return
    }
    const method = String(params.method ?? "GET").toUpperCase()
    const headers = (params.headers as Record<string, string>) ?? {}
    const body = params.body
    // `responseKey` is the legacy restful_data_action spelling of resultTarget
    // (read-tolerant cross-registry alias, like graphql's resultPath).
    // `resultKey` is the older corpus spelling that wrote under the
    // `_results.` slot — restore the prefix the downstream `#{_results.<key>}`
    // reads expect (reconcile-corpus-actions.mjs tier-1 rule).
    let target = (params.resultTarget ?? params.target ?? params.responseKey) as string | undefined
    if (target == null && params.resultKey != null) {
      const v = String(params.resultKey)
      target = v.startsWith("_results.") ? v : `_results.${v}`
    }
    const credentials = params.credentials as RequestCredentials | undefined
    const timeoutMs = params.timeout == null ? NaN : Number(params.timeout)
    const retryPolicy =
      params.retryPolicy && typeof params.retryPolicy === "object"
        ? (params.retryPolicy as Record<string, unknown>)
        : undefined
    const configuredAttempts = Number(retryPolicy?.maxAttempts ?? 1)
    const maxAttempts =
      Number.isInteger(configuredAttempts) && configuredAttempts > 0
        ? configuredAttempts
        : 1
    const backoffMs = Math.max(0, Number(retryPolicy?.backoffMs ?? 0))
    const retryableStatusCodes = new Set(
      Array.isArray(retryPolicy?.retryableStatusCodes)
        ? retryPolicy.retryableStatusCodes.map(Number)
        : [],
    )
    const retryOnTimeout = retryPolicy?.retryOnTimeout === true
    const waitForRetry = async (attempt: number): Promise<void> => {
      const delayMs = backoffMs * attempt
      if (delayMs <= 0) return
      const wait = new Promise<void>((resolve) => setTimeout(resolve, delayMs))
      await (context ? this.raceWithExecutionControl(wait, context) : wait)
    }

    const hasVaultRef = (str: string) => /#\{(env|props)\.vault\.[a-zA-Z0-9_-]+\}/.test(str)
    const bodyStr = body ? JSON.stringify(body) : ""
    const needsProxy = hasVaultRef(url) || Object.values(headers).some(hasVaultRef) || hasVaultRef(bodyStr)

    let resp: Response | undefined
    let data: unknown = null
    for (let attempt = 1; attempt <= maxAttempts; attempt += 1) {
      let abortTimer: ReturnType<typeof setTimeout> | undefined
      let signal: AbortSignal | undefined
      let attemptTimedOut = false
      const executionControl = context ? this.executionControl(context) : null
      const needsAbortController =
        (Number.isFinite(timeoutMs) && timeoutMs > 0) ||
        Boolean(executionControl)
      let abortFromExecution: (() => void) | undefined
      if (needsAbortController) {
        const controller = new AbortController()
        signal = controller.signal
        if (executionControl) {
          abortFromExecution = () => controller.abort(executionControl.signal.reason)
          if (executionControl.signal.aborted) abortFromExecution()
          else
            executionControl.signal.addEventListener(
              "abort",
              abortFromExecution,
              { once: true },
            )
        }
        if (Number.isFinite(timeoutMs) && timeoutMs > 0) {
          abortTimer = setTimeout(() => {
            attemptTimedOut = true
            controller.abort()
          }, timeoutMs)
        }
      }
      let attemptResponse: Response
      let attemptData: unknown = null
      let bodyReadTimedOut = false
      try {
        if (needsProxy) {
          const platformUrl = String(this.state.get("env.COGO_PLATFORM_URL") ?? "https://platform.cogo.xyz")
          const proxyUrl = `${platformUrl}/api/v2/proxy`

          const proxyHeaders: Record<string, string> = {
            "Content-Type": "application/json",
          }

          const auth = this.state.get("session.access_token") || this.state.get("props.authJwt")
          if (auth) {
            proxyHeaders["Authorization"] = `Bearer ${auth}`
          }

          attemptResponse = await fetch(proxyUrl, {
            method: "POST",
            headers: proxyHeaders,
            body: JSON.stringify({
              url,
              method,
              headers,
              body,
            }),
            signal,
          })
        } else {
          const fetchOptions: RequestInit = { method, headers }
          if (signal) fetchOptions.signal = signal
          if (credentials) fetchOptions.credentials = credentials
          if (body && method !== "GET") {
            fetchOptions.body = JSON.stringify(body)
            if (!headers["Content-Type"]) {
              ;(fetchOptions.headers as Record<string, string>)["Content-Type"] = "application/json"
            }
          }
          attemptResponse = await fetch(url, fetchOptions)
        }

        attemptData = await attemptResponse.json().catch(() => {
          bodyReadTimedOut = signal?.aborted === true
          return null
        })
      } catch (error) {
        if (executionControl?.signal.aborted) {
          throw executionAbortError(executionControl)
        }
        const timedOut = attemptTimedOut
        if (timedOut && retryOnTimeout && attempt < maxAttempts) {
          await waitForRetry(attempt)
          continue
        }
        if (timedOut) {
          throw new Error(`REST_API_TIMEOUT:${timeoutMs}`)
        }
        throw error
      } finally {
        if (abortTimer) clearTimeout(abortTimer)
        if (abortFromExecution && executionControl) {
          executionControl.signal.removeEventListener(
            "abort",
            abortFromExecution,
          )
        }
      }
      if (executionControl?.signal.aborted) {
        throw executionAbortError(executionControl)
      }
      if (bodyReadTimedOut && retryOnTimeout) {
        if (attempt < maxAttempts) {
          await waitForRetry(attempt)
          continue
        }
        throw new Error(`REST_API_TIMEOUT:${timeoutMs}`)
      }
      if (bodyReadTimedOut) {
        throw new Error(`REST_API_TIMEOUT:${timeoutMs}`)
      }
      if (
        !attemptResponse.ok &&
        retryableStatusCodes.has(attemptResponse.status) &&
        attempt < maxAttempts
      ) {
        await waitForRetry(attempt)
        continue
      }
      resp = attemptResponse
      data = attemptData
      break
    }
    if (!resp) throw new Error("REST_API_RETRY_EXHAUSTED")
    if (params.throwOnHttpError === true && !resp.ok) {
      throw new Error(`REST_API_HTTP_ERROR:${resp.status}`)
    }

    if (target) {
      // Parse Content-Range header for PostgREST count support
      const contentRange = resp.headers.get("content-range")
      let count: number | null = null
      if (contentRange) {
        const parts = contentRange.split("/")
        const total = parts[1]
        if (total && total !== "*") count = parseInt(total, 10)
      }

      // Normalize response so #{_results.*.data} resolves correctly.
      // Cloudflare pg-proxy already wraps as {success, data, count}.
      // Raw PostgREST returns plain arrays. Handle both cases:
      let wrapped: Record<string, unknown>
      if (data && typeof data === "object" && !Array.isArray(data) && "data" in data) {
        // Already wrapped (e.g., pg-proxy response {success, data, count}) — use as-is
        // but merge count from Content-Range if proxy didn't include it
        wrapped = { ...data, status: resp.status }
        if (count != null && wrapped.count == null) wrapped.count = count
      } else {
        // Raw response (array or primitive) — wrap for #{_results.*.data} pattern
        wrapped = { data, count, status: resp.status }
      }
      this.state.setState(target.replace(/^#/, ""), wrapped)
    }
  }

  private async actionDatasourceCall(params: Record<string, unknown>): Promise<unknown> {
    const datasourceId = params.datasourceId ?? params.source
    if (datasourceId != null) {
      await this.actionLoadDatasource({
        ...params,
        datasourceId,
      })
      return
    }
    return {
      sourceExpression: params.sourceExpression ?? null,
      status: "source-derived-preview-noop",
    }
  }

  private actionSetVariable(params: Record<string, unknown>): void {
    const name = String(params.name ?? params.target ?? "")
    const value = params.value
    if (!name) return
    // `scope` (opt-in) routes WHERE the variable is written. Omitted ⇒ the bare
    // write (the validator-required setState target form). "local" ⇒ explicit
    // local.<name>; "app"/"appData"/"global" ⇒ appData.<name> (app-wide);
    // "computed" ⇒ computed.<name>; any other value is used verbatim as the
    // namespace prefix. Reading params.scope directly keeps the executor analyzable.
    const scope = params.scope != null ? String(params.scope) : ""
    const SCOPE_NS: Record<string, string> = {
      app: "appData", appData: "appData", global: "appData", local: "local", computed: "computed",
    }
    const target = scope ? `${SCOPE_NS[scope] ?? scope}.${name}` : name
    this.state.setState(target.replace(/^#/, ""), value)
  }

  private actionLog(params: Record<string, unknown>): void {
    const level = String(params.level ?? "info")
    const message = params.message ?? params.value
    // Optional structured payload logged alongside the message (like a second
    // console.* argument). Spread a local args array — never `...params` — so the
    // static param-parser keeps the executor analyzable.
    const data = params.data
    const args = data !== undefined ? ["[cogo-v2]", message, data] : ["[cogo-v2]", message]
    switch (level) {
      case "warn": console.warn(...args); break
      case "error": console.error(...args); break
      default: console.log(...args)
    }
  }

  private actionStopFlow(params: Record<string, unknown>): never {
    // `reason` (optional) annotates WHY the flow halted; surfaced on the trace by
    // the executeFlowImmediate catch.
    throw { __stopFlow: true, reason: params.reason }
  }

  /**
   * dispatchStoreAction — invoke a host-registered handler by name.
   *
   * params.handler: string — must be pre-registered via StoreActionRegistry.
   * params.params?: object — passed as-is to the handler.
   *
   * Unknown handler / missing registry / non-string handler name all produce
   * a console.warn and resolve without throwing, so a buggy flow cannot crash
   * the app. Registered handler exceptions propagate normally.
   */
  private async actionDispatchStoreAction(params: Record<string, unknown>): Promise<void> {
    const name = typeof params.handler === "string" ? params.handler : null
    if (!name) {
      console.warn("[runtime-v2] dispatchStoreAction: missing or non-string 'handler' param", params)
      return
    }
    const registry = this.storeActionRegistry
    if (!registry) {
      console.warn(`[runtime-v2] dispatchStoreAction: no StoreActionRegistry attached (handler="${name}")`)
      return
    }
    const handler = registry.get(name)
    if (!handler) {
      console.warn(
        `[runtime-v2] dispatchStoreAction: unknown handler "${name}" — not in registry. ` +
          `Known: [${registry.list().join(", ")}]`,
      )
      return
    }
    const handlerParams = params.params && typeof params.params === "object"
      ? (params.params as Record<string, unknown>)
      : undefined
    await handler(handlerParams)
  }

  private async actionCallFlow(params: Record<string, unknown>, parentContext?: Record<string, unknown>): Promise<void> {
    const flowName = params.flow as string ?? params.flowName as string
    const flow = this.findFlow(flowName)
    if (!flow) return
    const ctx = this.contextForFlow(flow, (params.context as Record<string, unknown>) ?? parentContext ?? {})
    // `await: false` → fire-and-forget. Run the detached sub-flow on a trace-isolated
    // VIEW of this engine (own null activeTrace / empty parent stack). State, platform,
    // compute, action handlers, and the flow registry are all still shared (they live
    // on the prototype / are closed over the original engine), so behavior is
    // identical — only the trace bookkeeping is private. This prevents the detached
    // sub-flow, when it resumes after this method returns, from writing its steps into
    // the caller's trace (already restored by executeFlowWithTrace) or into a different
    // flow's trace that happens to be active on this engine. A rejection handler keeps
    // an async sub-flow failure from becoming an unhandled promise rejection.
    // (Residual: a NESTED callFlow inside the detached sub re-enters via the shared
    // handler and is traced on the original engine — narrow; full isolation needs
    // trace-context threading.)
    if (params.await === false) {
      const detached = Object.create(this) as ActionFlowEngine
      detached.activeTrace = null
      detached.traceParentStack = []
      detached.traceStepIndex = 0
      void detached.executeFlowImmediate(flow, ctx).catch(() => {})
      return
    }
    // `await` (default true): block until the sub-flow finishes; it shares this
    // engine's trace so its steps nest into the caller trace.
    await this.executeFlowImmediate(flow, ctx)
  }

  private actionCompute(params: Record<string, unknown>): unknown {
    const target = params.target as string | undefined
    if (!target) return undefined

    // value form: object/array literal already deep-resolved by resolveParams.
    // Takes precedence over expression form when both are present.
    if (params.value !== undefined) {
      this.state.setState(target.replace(/^#/, ""), params.value)
      return params.value
    }

    // expression form: evaluate compute-evaluator string.
    if (params.expression) {
      const result = this.compute.evaluate(String(params.expression))
      this.state.setState(target.replace(/^#/, ""), result)
      return result
    }

    return undefined
  }

  /**
   * Validate `params.target` against `params.rules` (array of {type, value?, message?}).
   * Returns {valid: boolean, message?: string}. First failing rule wins.
   * Unknown rule types are treated as no-ops (forward-compat).
   */
  private actionValidate(params: Record<string, unknown>): { valid: boolean; message?: string } {
    const target = params.target
    const rules = Array.isArray(params.rules) ? (params.rules as Array<Record<string, unknown>>) : []

    for (const rule of rules) {
      const ruleType = String(rule.type ?? "")
      const message = rule.message != null ? String(rule.message) : undefined
      const ruleValue = rule.value

      let pass = true

      switch (ruleType) {
        case "required":
          pass = target !== null && target !== undefined && target !== ""
          break

        case "minLength": {
          const min = Number(ruleValue)
          const len = typeof target === "string" || Array.isArray(target)
            ? (target as { length: number }).length
            : 0
          pass = len >= min
          break
        }

        case "maxLength": {
          const max = Number(ruleValue)
          const len = typeof target === "string" || Array.isArray(target)
            ? (target as { length: number }).length
            : 0
          pass = len <= max
          break
        }

        case "pattern": {
          const re = String(ruleValue ?? "")
          let regex: RegExp | null = null
          try {
            regex = new RegExp(re)
          } catch {
            regex = null  // bad regex → treat as pass (forward-compat)
          }
          pass = regex == null ? true : regex.test(String(target))
          break
        }

        case "email":
          pass = /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(String(target))
          break

        case "min": {
          const min = Number(ruleValue)
          pass = Number(target) >= min
          break
        }

        case "max": {
          const max = Number(ruleValue)
          pass = Number(target) <= max
          break
        }

        default:
          // Unknown rule types pass silently (forward-compat).
          pass = true
      }

      if (!pass) {
        return { valid: false, ...(message ? { message } : {}) }
      }
    }

    return { valid: true }
  }

  private actionShowPopup(params: Record<string, unknown>): void {
    if (this.onShowPopup) {
      // actions (opt-in) — an array of dialog action buttons forwarded to the host.
      // Passed only when present so the common case stays a plain {title,message,type}.
      const actions = Array.isArray(params.actions) ? params.actions : undefined
      this.onShowPopup({
        title: String(params.title ?? ""),
        message: String(params.message ?? ""),
        type: params.type ? String(params.type) : undefined,
        ...(actions ? { actions } : {}),
      })
    }
  }

  private actionShowLoading(params: Record<string, unknown>): void {
    if (this.onShowLoading) {
      const show = Boolean(params.show ?? params.value ?? true)
      // Optional loading-indicator text + full-screen overlay flag forwarded to the
      // host. Pass the options arg only when one is set so the common case stays a
      // clean single-arg call (no behavior change).
      const message = params.message != null ? String(params.message) : undefined
      const overlay = params.overlay != null ? Boolean(params.overlay) : undefined
      if (message !== undefined || overlay !== undefined) {
        this.onShowLoading(show, { message, overlay })
      } else {
        this.onShowLoading(show)
      }
    }
  }

  /**
   * openModal (Lane B-5, reconciled show_popup_action) — open a NAMED modal via
   * the host modal opener. Lifts the corpus `{popup, ...rest}` shape into
   * `(modalId, props)`: `popup`/`modalId` is the registry id, every other param is
   * a modal prop. Params arrive #{}-resolved (not a META_SUBACTION). No-op without
   * a modal id or an injected `onOpenModal` (headless-safe). The named modals are
   * not yet declared in the blocks' ui.cogo.json — the host modal-registry wiring
   * is a renderer-owner follow-up (the corpus follow-up authors the modals).
   */
  private actionOpenModal(params: Record<string, unknown>): void {
    const modalId = String(params.modalId ?? params.popup ?? "")
    if (!modalId) return
    const props: Record<string, unknown> = {}
    for (const key of Object.keys(params)) {
      if (key !== "modalId" && key !== "popup") props[key] = params[key]
    }
    if (this.onOpenModal) this.onOpenModal(modalId, props)
  }

  /**
   * invokeCallback (Lane B-5, reconciled callback_action) — invoke a host-
   * registered named handler (corpus `onApprove`/`onReject`). Normalizes
   * `callbackId`→`callback` and `args`/`params`. No-op without a name or an
   * injected `onInvokeCallback` (headless-safe). The host wires a block-instance-
   * scoped registry (a global hook would shadow `onApprove` across cards).
   */
  private actionInvokeCallback(params: Record<string, unknown>, context?: Record<string, unknown>): void {
    const name = String(params.callback ?? params.callbackId ?? "")
    if (!name) return
    const args = params.args ?? params.params
    if (this.onInvokeCallback) {
      this.onInvokeCallback(
        name,
        args,
        context ? this.cleanupExecutionContext(context) : context,
      )
    }
  }

  /**
   * mediaControl (Lane B-7) — imperative command on the block's media element
   * (corpus video-player play/pause/seekPercent/seekRelative/setVolume/setMuted/
   * toggleFullscreen). Each corpus call pairs with a setState that drives the
   * reactive UI; this is the side-effect on the live <video>. Lifts the corpus
   * `{ command, ...args }` into `(command, args)`. No result is read. No-op
   * without a command or an injected `onMediaControl` (headless-safe). The host
   * wires a block-instance-scoped media-element-ref-by-id registry (a global hook
   * would command the wrong player) — a renderer-owner follow-up.
   */
  private actionMediaControl(params: Record<string, unknown>, context?: Record<string, unknown>): void {
    const command = String(params.command ?? "")
    if (!command) return
    const args: Record<string, unknown> = {}
    for (const key of Object.keys(params)) {
      if (key !== "command") args[key] = params[key]
    }
    if (this.onMediaControl) {
      this.onMediaControl(
        command,
        args,
        context ? this.cleanupExecutionContext(context) : context,
      )
    }
  }

  private async actionRunMotion(params: Record<string, unknown>, context?: Record<string, unknown>): Promise<unknown> {
    const target = String(params.target ?? "")
    const preset = String(params.preset ?? "")
    if (!target || !preset || !this.onRunMotion) return { status: "skipped" }
    return await this.onRunMotion(
      params,
      context ? this.cleanupExecutionContext(context) : context,
    )
  }

  private actionGoLink(params: Record<string, unknown>): void {
    const url = String(params.url ?? "")
    const newTab = Boolean(params.newTab ?? true)
    if (this.platform?.openExternal) {
      this.platform.openExternal(url, newTab)
    }
  }

  private async actionCopyToClipboard(params: Record<string, unknown>): Promise<void> {
    const text = String(params.text ?? params.value ?? "")
    if (this.platform?.copyToClipboard) {
      await this.platform.copyToClipboard(text)
    }
  }

  private actionScrollToPosition(params: Record<string, unknown>): void {
    const y = Number(params.y ?? 0)
    const smooth = Boolean(params.smooth ?? params.behavior !== "instant")
    if (this.platform) {
      this.platform.scrollToPosition(y, smooth)
    }
  }

  // Lane B-1: scroll to an element by id. Canonical param `target`; the corpus
  // emits `sectionId` (read alias). Ported from v1 @cogo/runtime-core actionScrollTo
  // (target branch). smooth resolution mirrors actionScrollToPosition.
  private actionScrollToElement(params: Record<string, unknown>): void {
    const target = (params.target ?? params.sectionId) as string | undefined
    if (!target) return
    const smooth = Boolean(params.smooth ?? params.behavior !== "instant")
    if (this.platform) {
      this.platform.scrollToElement(target, smooth)
    }
  }

  // Lane B-2: apply a named theme via the host (data-theme attr / root class).
  // The corpus block emits a theme name and reads nothing back, so this is a pure
  // host-side effect. platform.setTheme is OPTIONAL (mirrors `confirm?`) — no-op
  // when the adapter (or the whole platform) doesn't provide it.
  private actionSetTheme(params: Record<string, unknown>): void {
    const theme = params.theme as string | undefined
    if (!theme) return
    this.platform?.setTheme?.(theme)
  }

  // Lane B-3: broadcast a named app event. Two fan-outs:
  //  (1) the host event bus (onEmitEvent host-hook) — component/host subscribers by name;
  //  (2) flows that subscribe via `flow.on === name` — re-triggered ASYNC (next event-loop
  //      turn) so an emit→handle→emit chain is event-loop-paced: it yields between turns and
  //      never recurses synchronously. This is the loop-back that makes emission-paced loops
  //      (and event-driven agent loops) work without a blocking loop construct.
  // The corpus emits {event:"<name>"} (emitEvent) and {event,data} (emit alias); accept the
  // mixed eventName/payload spellings. No-op when no name (headless-safe).
  private actionEmitEvent(params: Record<string, unknown>): void {
    const name = (params.event ?? params.eventName ?? params.name) as string | undefined
    if (!name) return
    const payload = params.data ?? params.payload
    // (1) host bus
    this.onEmitEvent?.(name, payload)
    // (2) flow loop-back — async, fire-and-forget; bounding is the flow's own guard's job
    const listeners = this.getAppEventFlows().get(name)
    if (!listeners?.length) return
    const ctx = { event: (payload ?? {}) as Record<string, unknown> }
    for (const flow of listeners) {
      setTimeout(() => {
        void Promise.resolve(this.executeFlow(flow, ctx)).catch((err) =>
          console.error("[ActionFlowEngine emitEvent→flow]", flow.name ?? "(anon)", err),
        )
      }, 0)
    }
  }

  // ---- Gemini AI Action (ported from v1) ----

  private async actionGemini(params: Record<string, unknown>): Promise<void> {
    const apiKey = String(params.apiKey ?? '')
    const model = String(params.model ?? 'gemini-2.5-pro')
    const prompt = String(params.prompt ?? '')
    const exposedActions = (params.exposedActions as string[]) ?? []
    const resultTarget = String(params.resultTarget ?? '_results.gemini').replace(/^#/, '')
    const maxToolCalls = Number(params.maxToolCalls ?? 5)

    const client = new GeminiClient({ apiKey, model })
    const callbackHandler = new ToolCallbackHandler(
      async (actionName, actionParams) => {
        const action = this.builtinActions[actionName]
        if (!action) throw new Error(`Unknown action: ${actionName}`)
        await action(actionParams)
        return this.state.get(resultTarget) ?? {}
      },
    )

    const tools = exposedActions.length > 0
      ? [{ functionDeclarations: buildFunctionDeclarations(exposedActions) }]
      : undefined

    const history = (this.state.get('_gemini.history') as GeminiContent[]) ?? []
    const contents: GeminiContent[] = [
      ...history,
      { role: 'user', parts: [{ text: prompt }] },
    ]

    let currentContents: GeminiContent[] = contents
    let toolCallCount = 0

    while (toolCallCount < maxToolCalls) {
      const result = await client.generateContent({
        contents: currentContents,
        ...(tools && { tools }),
      })

      if (result.functionCall) {
        toolCallCount++
        const actionResult = await callbackHandler.handle(result.functionCall)
        const functionResponseContent = callbackHandler.buildFunctionResponseContent(
          actionResult.name,
          actionResult.response,
        )
        currentContents = [
          ...currentContents,
          { role: 'model', parts: [{ functionCall: result.functionCall }] } as GeminiContent,
          functionResponseContent,
        ]
      } else {
        this.state.setState('_gemini.history', currentContents)
        this.state.setState(resultTarget, {
          type: 'text',
          response: result.text ?? '',
          toolCallsUsed: toolCallCount,
        })
        return
      }
    }

    this.state.setState(resultTarget, {
      type: 'error',
      error: `Max tool calls (${maxToolCalls}) reached`,
      toolCallsUsed: toolCallCount,
    })
  }

  // ---- Gemini Function Call Agent Loop ----

  private async actionGeminiFunctionCall(params: Record<string, unknown>): Promise<void> {
    const { agentLoop } = await import('./gemini')
    const apiKey = String(params.apiKey ?? '')
    const model = String(params.model ?? 'gemini-2.5-flash')
    const prompt = String(params.prompt ?? '')
    const maxTurns = Number(params.maxTurns ?? 5)
    const tokenBudget = Number(params.tokenBudget ?? 20000)
    const systemPrompt = params.systemPrompt ? String(params.systemPrompt) : undefined
    const target = String(params.resultTarget ?? '_results.agent').replace(/^#/, '')
    const toolNames = (params.tools as string[]) ?? []

    const client = new GeminiClient({ apiKey, model })
    const toolHandler = new ToolCallbackHandler(
      async (actionName, actionParams) => {
        const action = this.builtinActions[actionName]
        if (!action) throw new Error(`Unknown action: ${actionName}`)
        await action(actionParams)
        return this.state.get(target) ?? {}
      },
    )

    const tools = buildFunctionDeclarations(toolNames)

    try {
      const result = await agentLoop({
        client,
        toolHandler,
        prompt,
        tools,
        maxTurns,
        tokenBudget,
        systemPrompt,
      })

      this.state.setState(target, { data: result.text, error: null })
      this.state.setState('_results.agentHistory', result.history)
    } catch (err: any) {
      this.state.setState(target, { data: null, error: err.message ?? String(err) })
    }
  }

  // ---- Gemini Vision Action ----

  private async actionGeminiVision(params: Record<string, unknown>): Promise<void> {
    const { buildImageParts } = await import('./gemini-vision-action')
    const prompt = String(params.prompt ?? 'Describe this image.')
    const model = String(params.model ?? 'gemini-2.5-flash')
    const apiKey = String(params.apiKey ?? '')
    const target = String(params.resultTarget ?? '_results.vision').replace(/^#/, '')

    try {
      const imageParts = buildImageParts({
        imageUrl: params.imageUrl as string | undefined,
        imageBase64: params.imageBase64 as string | undefined,
        images: params.images as any[] | undefined,
        mimeType: params.mimeType as string | undefined,
      })

      const parts = [...imageParts, { text: prompt }]
      const client = new GeminiClient({ apiKey, model })

      const result = await client.generateContent({
        contents: [{ role: 'user', parts }],
      })

      this.state.setState(target, { data: result.text ?? '', error: null })
    } catch (err: any) {
      this.state.setState(target, { data: null, error: err.message ?? String(err) })
    }
  }

  // ── Platform-delegated actions (v18.0) ──

  private actionStorage(params: Record<string, unknown>): void {
    if (!this.platform) return
    const op = String(params.operation ?? "get")
    const key = String(params.key ?? "")
    const resultPath = String(params.resultPath ?? "")
    // secure (opt-in) — route get/set/remove through the platform's secure
    // (keychain / Keystore) storage when it provides one. The web platform has no
    // hardware-backed keychain, so its adapter omits these methods and the runtime
    // falls back to plain storage with a one-time warning; native adapters
    // (Keychain/Keystore) realize the encrypted-at-rest guarantee. `clear` has no
    // secure variant and always clears plain storage.
    const secure = params.secure === true || params.secure === "true"
    const hasSecure = !!(this.platform.getSecureItem && this.platform.setSecureItem && this.platform.removeSecureItem)
    if (secure && !hasSecure) {
      console.warn("[runtime-v2] storage: secure storage unavailable on this platform — falling back to plain storage")
    }
    const useSecure = secure && hasSecure
    if (op === "get") {
      const value = useSecure ? this.platform.getSecureItem!(key) : this.platform.getItem(key)
      if (resultPath) this.state.setState(resultPath, value)
    } else if (op === "set") {
      const value = String(params.value ?? "")
      if (useSecure) this.platform.setSecureItem!(key, value)
      else this.platform.setItem(key, value)
    } else if (op === "remove") {
      if (useSecure) this.platform.removeSecureItem!(key)
      else this.platform.removeItem(key)
    } else if (op === "clear") {
      this.platform.clearStorage()
    }
  }

  private async actionClipboardRead(params: Record<string, unknown>): Promise<void> {
    if (!this.platform) return
    const resultPath = String(params.resultPath ?? "_results.clipboard")
    const text = await this.platform.readClipboard()
    this.state.setState(resultPath, text)
  }

  private async actionShare(params: Record<string, unknown>): Promise<void> {
    if (!this.platform) return
    // files (opt-in) — Web Share API Level 2 attachments (navigator.share({files})).
    // Forwarded only when present so the L1 title/text/url path is unchanged.
    const files = Array.isArray(params.files) ? params.files : undefined
    await this.platform.share({
      title: params.title as string | undefined,
      text: params.text as string | undefined,
      url: params.url as string | undefined,
      ...(files ? { files } : {}),
    })
  }

  private async actionGeolocation(params: Record<string, unknown>): Promise<void> {
    if (!this.platform) return
    const resultPath = String(params.resultPath ?? "_results.geo")
    // accuracy/timeout (opt-in) — forwarded as PositionOptions. accuracy "high" ⇒
    // enableHighAccuracy; timeout is ms. Omitting both ⇒ getGeolocation() with no
    // options (current behavior).
    const options: { enableHighAccuracy?: boolean; timeout?: number } = {}
    if (params.accuracy != null) options.enableHighAccuracy = String(params.accuracy) === "high"
    if (params.timeout != null) options.timeout = Number(params.timeout)
    const opts = Object.keys(options).length > 0 ? options : undefined
    try {
      const pos = await this.platform.getGeolocation(opts)
      this.state.setState(resultPath, pos)
    } catch {
      this.state.setState(resultPath, { error: "geolocation_failed" })
    }
  }

  private async actionDownload(params: Record<string, unknown>): Promise<void> {
    if (!this.platform) return
    const url = String(params.url ?? "")
    const filename = String(params.filename ?? "download")
    // headers (opt-in) — platform.download has no header channel, so when headers
    // are supplied (e.g. an auth token) fetch the URL with them and download the
    // fetched bytes as a data: URL. Omitting headers ⇒ a direct platform.download.
    const headers = params.headers
    if (headers && typeof headers === "object") {
      try {
        const resp = await this.platform.fetch(url, { headers: headers as Record<string, string> })
        const buf = await resp.arrayBuffer()
        const contentType = resp.headers.get("content-type") ?? "application/octet-stream"
        this.platform.download(`data:${contentType};base64,${this.base64FromArrayBuffer(buf)}`, filename)
      } catch (e) {
        console.warn(`[runtime-v2] downloadAction: fetch-with-headers failed for ${url}`, e)
      }
      return
    }
    this.platform.download(url, filename)
  }

  /** Portable ArrayBuffer → base64 (btoa on web/edge, Buffer on Node). */
  private base64FromArrayBuffer(buf: ArrayBuffer): string {
    const bytes = new Uint8Array(buf)
    let bin = ""
    for (let i = 0; i < bytes.length; i++) bin += String.fromCharCode(bytes[i])
    if (typeof btoa !== "undefined") return btoa(bin)
    return (globalThis as { Buffer?: { from(b: Uint8Array): { toString(enc: string): string } } }).Buffer!
      .from(bytes).toString("base64")
  }

  private async actionFileUpload(params: Record<string, unknown>): Promise<void> {
    if (!this.platform) return
    const accept = params.accept as string | undefined
    const resultPath = String(params.resultPath ?? "_results.file")
    const file = await this.platform.pickFile(accept)
    // maxSize (opt-in) — reject a picked file larger than maxSize bytes. Size comes
    // from an explicit numeric `file.size` when present, else is derived from
    // `file.data` (data: URL base64 / raw string / ArrayBuffer view). When the size
    // can't be determined the file passes through (we don't reject what we can't
    // measure). On rejection an `{ error: "file_too_large", ... }` object is written
    // to resultPath instead of the file.
    const maxSize = Number(params.maxSize ?? 0)
    if (maxSize > 0 && file) {
      const size = this.fileByteSize(file)
      if (size != null && size > maxSize) {
        const name = (file as { name?: unknown }).name
        this.state.setState(resultPath, {
          error: "file_too_large",
          maxSize,
          size,
          ...(name != null ? { name } : {}),
        })
        return
      }
    }
    // url/field (opt-in) — when `url` is set, POST the picked file as multipart
    // (form field name from `field`, default "file") and store the parsed response
    // at resultPath. Omitting `url` ⇒ the picked file is stored as-is.
    const url = params.url != null ? String(params.url) : ""
    if (url && file) {
      const field = String(params.field ?? "file")
      try {
        const blob = this.fileToBlob(file as Record<string, unknown>)
        const name = (file as { name?: unknown }).name
        const form = new FormData()
        form.append(field, blob, name != null ? String(name) : "upload")
        const resp = await this.platform.fetch(url, { method: "POST", body: form })
        this.state.setState(resultPath, await this.parseResponse(resp))
      } catch (e) {
        this.state.setState(resultPath, { error: "upload_failed", message: (e as Error)?.message ?? String(e) })
      }
      return
    }
    this.state.setState(resultPath, file)
  }

  /** Convert a picked file ({ data } = Blob | ArrayBuffer | data:URL | string) to a Blob. */
  private fileToBlob(file: Record<string, unknown>): Blob {
    const d = file.data
    if (d instanceof Blob) return d
    if (d instanceof ArrayBuffer) return new Blob([d])
    if (ArrayBuffer.isView(d)) return new Blob([d as BlobPart])
    if (typeof d === "string") {
      if (d.startsWith("data:")) {
        const comma = d.indexOf(",")
        const meta = d.slice(5, comma) // e.g. "image/png;base64"
        const isB64 = /;base64$/i.test(meta)
        const contentType = meta.replace(/;base64$/i, "")
        const body = d.slice(comma + 1)
        if (isB64) {
          const bin = typeof atob !== "undefined"
            ? atob(body)
            : (globalThis as { Buffer?: { from(s: string, e: string): { toString(e: string): string } } }).Buffer!.from(body, "base64").toString("binary")
          const bytes = new Uint8Array(bin.length)
          for (let i = 0; i < bin.length; i++) bytes[i] = bin.charCodeAt(i)
          return new Blob([bytes], { type: contentType })
        }
        return new Blob([decodeURIComponent(body)], { type: contentType })
      }
      const type = typeof file.type === "string" ? file.type : undefined
      return new Blob([d], type ? { type } : undefined)
    }
    return new Blob([])
  }

  /** Parse an upload response — JSON when the content-type says so, else text. */
  private async parseResponse(resp: Response): Promise<unknown> {
    const ct = resp.headers.get("content-type") ?? ""
    if (ct.includes("application/json")) {
      try { return await resp.json() } catch { return await resp.text() }
    }
    return await resp.text()
  }

  /** Best-effort byte size of a picked file (a Blob/File, or a { size? } / { data } shape). */
  private fileByteSize(file: unknown): number | null {
    // The production WebPlatformAdapter.pickFile returns { name, data: <File> } where
    // the File extends Blob and carries .size — handle both a directly-passed Blob and
    // a { data: Blob } wrapper, else maxSize would be a no-op on every real web upload.
    if (file instanceof Blob) return file.size
    if (!file || typeof file !== "object") return null
    const f = file as Record<string, unknown>
    if (typeof f.size === "number") return f.size
    const d = f.data
    if (d instanceof Blob) return d.size
    if (typeof d === "string") {
      const comma = d.indexOf(",")
      if (d.startsWith("data:") && comma >= 0) {
        const meta = d.slice(5, comma)
        const body = d.slice(comma + 1)
        // Only a `;base64` data URL uses the base64-length formula; a plain
        // `data:<type>,<url-encoded>` body must be URL-decoded and measured (mirrors
        // fileToBlob), else size and the bytes actually uploaded would disagree.
        if (/;base64$/i.test(meta)) {
          const padding = body.endsWith("==") ? 2 : body.endsWith("=") ? 1 : 0
          return Math.max(0, Math.floor((body.length * 3) / 4) - padding)
        }
        try {
          return new TextEncoder().encode(decodeURIComponent(body)).length
        } catch {
          return new TextEncoder().encode(body).length
        }
      }
      return new TextEncoder().encode(d).length
    }
    if (d instanceof ArrayBuffer) return d.byteLength
    if (ArrayBuffer.isView(d)) return (d as ArrayBufferView).byteLength
    return null
  }

  // ──────────────────────────────────────────────────────────────────────────
  // Lane B-6 — file pickers + multipart upload + image crop. Reconcile the
  // form/image-upload + form/file-upload corpus blocks onto real executors.
  // ──────────────────────────────────────────────────────────────────────────

  /**
   * Open a (multi-)file picker and write the chosen files as `{ files }` to
   * resultPath (default `_results.pickFiles` — the corpus reads
   * `#{_results.pickFiles.files}`). Auto-capture is keyed off step.name, which the
   * corpus step does not set, so this writes the result explicitly (mirrors
   * actionFileUpload). Headless / no picker capability → `{ files: [] }`. Falls
   * back to the single-file `platform.pickFile` when `pickFiles` is unavailable.
   */
  private async actionPickFiles(params: Record<string, unknown>): Promise<void> {
    const resultPath = String(params.resultPath ?? params.resultTarget ?? "_results.pickFiles").replace(/^#/, "")
    const accept = params.accept as string | undefined
    const multiple = params.multiple !== false
    let files: unknown[] = []
    if (this.platform?.pickFiles) {
      files = (await this.platform.pickFiles(accept, multiple)) ?? []
    } else if (this.platform?.pickFile) {
      const f = await this.platform.pickFile(accept)
      files = f ? [f] : []
    }
    this.state.setState(resultPath, { files })
  }

  /**
   * Trigger the native file-picker dialog (fire-and-forget). The selected files
   * are delivered to the block via a separate host-dispatched drop/change event
   * (the file-upload block's handleFileDrop), NOT captured here — so this writes
   * no state, keeping a block that declares no `_results` output
   * SecurityError-free. Headless / no picker capability → no-op.
   */
  private async actionOpenNativeFilePicker(params: Record<string, unknown>): Promise<void> {
    if (!this.platform?.pickFiles) return
    const accept = params.accept as string | undefined
    const multiple = params.multiple !== false
    await this.platform.pickFiles(accept, multiple)
  }

  /**
   * Datasource-backed multipart upload. Delegates to the Lane B-4 loadDatasource
   * override path — the named `source` def is POSTed with the files as the body.
   * No `this.datasources` (headless) or no source → no-op. A resultTarget is
   * honored when supplied (the corpus currently supplies none — fire-and-forget).
   */
  private async actionUploadFiles(params: Record<string, unknown>): Promise<void> {
    const source = params.source ?? params.datasourceId
    if (source == null) return
    const rawTarget = params.resultTarget ?? params.target
    await this.actionLoadDatasource({
      datasourceId: source,
      method: "POST",
      body: { files: params.files },
      ...(rawTarget != null ? { resultTarget: rawTarget } : {}),
    })
  }

  /**
   * Crop an image (by id) to a ratio — host-side canvas op. Fire-and-forget: the
   * corpus does not read a result (the next step just clears selectedImage).
   * Headless / no crop capability → no-op.
   */
  private async actionCropImage(params: Record<string, unknown>): Promise<void> {
    const imageId = params.imageId
    if (imageId == null) return
    const ratio = params.ratio != null ? String(params.ratio) : undefined
    await this.platform?.cropImage?.(String(imageId), ratio)
  }

  private actionNotification(params: Record<string, unknown>): void {
    if (!this.platform) return
    const title = String(params.title ?? "")
    const body = params.body as string | undefined
    this.platform.showNotification(title, body)
  }

  private async actionCrypto(params: Record<string, unknown>): Promise<void> {
    if (!this.platform) return
    const algorithm = String(params.algorithm ?? "SHA-256")
    const data = String(params.data ?? "")
    const resultPath = String(params.resultPath ?? "_results.crypto")
    // operation (opt-in) selects the primitive; key supplies keying material for
    // keyed operations (encrypt / decrypt / sign). Omitting operation — or a
    // "digest"/"hash" operation — uses the one-way hash path (current behavior).
    // Keyed/symmetric operations require the platform's cryptoOperation primitive
    // (WebCrypto on web); without it the runtime degrades to a digest.
    const operation = params.operation != null ? String(params.operation) : ""
    const key = params.key != null ? String(params.key) : undefined
    const isKeyed = operation !== "" && operation !== "digest" && operation !== "hash"
    if (isKeyed && this.platform.cryptoOperation) {
      try {
        const out = await this.platform.cryptoOperation(operation, algorithm, data, key)
        this.state.setState(resultPath, out)
      } catch (e) {
        this.state.setState(resultPath, { error: "crypto_failed", message: (e as Error)?.message ?? String(e) })
      }
      return
    }
    const hash = await this.platform.digest(algorithm, data)
    this.state.setState(resultPath, hash)
  }

  private actionPrint(): void {
    this.platform?.print()
  }

  private actionAnalytics(params: Record<string, unknown>): void {
    if (!this.platform) return
    const event = String(params.event ?? "")
    const data = params.data as Record<string, unknown> | undefined
    this.platform.dispatchAnalyticsEvent(event, data)
  }

  private async actionOpenAuthSession(params: Record<string, unknown>): Promise<void> {
    if (!this.platform?.openAuthSession) {
      // Fallback: open in external browser
      this.platform?.openExternal(String(params.url ?? ""), true)
      return
    }
    const url = String(params.url ?? "")
    const redirectUrl = params.redirectUrl as string | undefined
    const resultTarget = String(params.resultTarget ?? "_results.auth")
    try {
      const result = await this.platform.openAuthSession(url, redirectUrl)
      this.state.setState(resultTarget, result)
    } catch {
      this.state.setState(resultTarget, { type: "error" })
    }
  }

  /** Payment action — delegates to onPayment handler or platform */
  onPayment: ((params: Record<string, unknown>) => Promise<Record<string, unknown>>) | null = null

  private async actionPayment(params: Record<string, unknown>): Promise<void> {
    if (!this.onPayment) return
    const resultTarget = String(params.resultTarget ?? "_results.payment")
    try {
      const result = await this.onPayment(params)
      this.state.setState(resultTarget, result)
    } catch (err: any) {
      this.state.setState(resultTarget, { error: err.message ?? String(err) })
    }
  }

  /** Web3 action — delegates to onWeb3 handler (host-injected; no-op when absent) */
  onWeb3: ((params: Record<string, unknown>) => Promise<Record<string, unknown>>) | null = null

  private async actionWeb3(params: Record<string, unknown>): Promise<void> {
    if (!this.onWeb3) return
    const resultTarget = String(params.resultTarget ?? "_results.web3")
    try {
      const result = await this.onWeb3(params)
      this.state.setState(resultTarget, result)
    } catch (err: any) {
      this.state.setState(resultTarget, { error: err.message ?? String(err) })
    }
  }

  /**
   * loadDatasource — resolve a named block/app datasource via the injected
   * DataSourceManager and (when a target is given) store its data in state.
   * Canonical generation params (action-types.json): `datasourceId` (required) +
   * `resultTarget` (optional); legacy `source`/`target` (v1 cogo-app.tsx wiring)
   * accepted as read-tolerant aliases. Mirrors the v1 reference handler: the
   * manager always loads+caches the source, but state is written ONLY when an
   * explicit (declared) target is supplied — omitting it avoids a strict
   * stateContract SecurityError on an undeclared path. No-op without an injected
   * manager (the host wires it from manifest/block sources). Async:
   * DataSourceManager.load fetches for rest/supabase/gemini sources.
   */
  private async actionLoadDatasource(params: Record<string, unknown>): Promise<void> {
    if (!this.datasources) return
    const id = String(params.datasourceId ?? params.source ?? "")
    if (!id) return
    const rawTarget = params.resultTarget ?? params.target
    const target = rawTarget != null ? String(rawTarget).replace(/^#/, "") : null
    // Rest-subset override (Lane B-4, reconciled restful_data_action): a step that
    // carries method/body/query is a flow-invoked REST call against the named def,
    // NOT the idempotent cached read. Resolve the def-level url #{} here (the
    // DataSourceManager has no expression evaluator), merge the step overrides, and
    // load via the UNCACHED loadSource so a mutation/paginated GET is never served
    // the cached first page. Params arrive already #{}-resolved by resolveParams
    // (loadDatasource is not a META_SUBACTION).
    const method = params.method != null ? String(params.method) : undefined
    const body = params.body
    const query = params.query
    const hasOverride =
      body !== undefined || query !== undefined || (method != null && method.toUpperCase() !== "GET")
    try {
      let data: unknown
      if (hasOverride) {
        const def = this.datasources.getSource(id)
        if (!def) throw new Error(`DataSource not found: ${id}`)
        const rdef: Record<string, unknown> = { ...(def as unknown as Record<string, unknown>) }
        if (typeof rdef.url === "string") rdef.url = this.resolveValue(rdef.url, {})
        if (typeof rdef.endpoint === "string") rdef.endpoint = this.resolveValue(rdef.endpoint, {})
        if (method !== undefined) rdef.method = method
        if (body !== undefined) rdef.body = body
        if (query !== undefined) rdef.query = query
        data = await this.datasources.loadSource(
          rdef as unknown as Parameters<DataSourceManager["loadSource"]>[0],
          id,
        )
      } else {
        data = await this.datasources.load(id)
      }
      if (target) this.state.setState(target, data)
    } catch (err: any) {
      if (target) this.state.setState(target, { error: err.message ?? String(err) })
    }
  }

  // ──────────────────────────────────────────────────────────────────────────
  // Lane B-4 — execution-control + graphql executors (faithful ports of the v1
  // @cogo/runtime-core builtins; the meta-actions dispatch a named sub-action via
  // resolveBuiltin). These were quarantined gen actions with no runtime-v2 builtin.
  // ──────────────────────────────────────────────────────────────────────────

  /** GraphQL query — POST {query, variables}; store data (or error) at resultTarget. */
  private async actionGraphql(params: Record<string, unknown>): Promise<void> {
    const url = String(params.url ?? params.endpoint ?? "")
    const query = String(params.query ?? "")
    const variables = (params.variables as Record<string, unknown>) ?? {}
    const headers = (params.headers as Record<string, string>) ?? {}
    // @cogo/action-registry emits `resultPath`; action-types.json uses
    // `resultTarget` — accept both (read-tolerant cross-registry alias).
    const resultTarget = String(params.resultTarget ?? params.resultPath ?? "_results.graphql").replace(/^#/, "")
    try {
      const res = await fetch(url, {
        method: "POST",
        headers: { "Content-Type": "application/json", ...headers },
        body: JSON.stringify({ query, variables }),
      })
      const json = (await res.json()) as Record<string, unknown>
      if (json.errors) {
        this.state.setState(resultTarget, { error: (json.errors as Array<{ message?: string }>)[0]?.message ?? "GraphQL error" })
      } else {
        this.state.setState(resultTarget, json.data)
      }
    } catch (err: any) {
      this.state.setState(resultTarget, { error: err?.message ?? String(err) })
    }
  }

  /**
   * Batch — run a list of named sub-actions sequentially. Awaits each (improves
   * on v1's fire-and-forget): async sub-actions complete in order and a rejection
   * propagates to flow error handling instead of escaping unhandled.
   */
  private async actionBatch(params: Record<string, unknown>, context?: Record<string, unknown>): Promise<void> {
    const actions = (params.actions ?? []) as Array<{ action: string; params?: Record<string, unknown> }>
    // stopOnError (default true) — when false, a rejected sub-action is caught and
    // the batch continues with the remaining sub-actions (best-effort), instead of
    // aborting the batch on the first throw.
    const stopOnError = params.stopOnError !== false
    for (const sub of actions) {
      if (!this.resolveBuiltin(sub.action)) continue
      if (stopOnError) {
        await this.dispatchSubAction(sub.action, (sub.params ?? {}) as Record<string, unknown>, context)
      } else {
        try {
          await this.dispatchSubAction(sub.action, (sub.params ?? {}) as Record<string, unknown>, context)
        } catch (e) {
          console.warn(`[runtime-v2] batchAction: sub-action "${sub.action}" failed (stopOnError=false), continuing`, e)
        }
      }
    }
  }

  /** Conditional delay — wait `duration` ms only when `condition` is truthy. */
  private async actionConditionalDelay(params: Record<string, unknown>): Promise<void> {
    // A string condition is an expression (schema/TBox form, e.g. "count > 0",
    // "false") — evaluate it like v1 did; a resolved value is truthiness-checked
    // directly. Without this, isTruthy treats every non-empty string as truthy.
    const raw = params.condition
    const evalCond = (): boolean => {
      const r = typeof raw === "string" ? this.compute.evaluate(raw) : raw
      return isTruthy(r)
    }
    // Poll mode (opt-in): when `pollInterval` is set, re-evaluate `condition` every
    // pollInterval ms until it becomes truthy, giving up after `maxWait` ms
    // (default 30000). This is the poll-until-truthy semantic; omitting pollInterval
    // keeps the legacy one-shot wait-while-truthy below.
    const pollInterval = Number(params.pollInterval ?? 0)
    if (pollInterval > 0) {
      const maxWait = Number(params.maxWait ?? 30000)
      const start = Date.now()
      while (!evalCond()) {
        if (Date.now() - start >= maxWait) return
        await new Promise<void>((resolve) => setTimeout(resolve, pollInterval))
      }
      return
    }
    if (!evalCond()) return
    const ms = Number(params.duration ?? params.ms ?? 0)
    if (ms > 0) await new Promise<void>((resolve) => setTimeout(resolve, ms))
  }

  /** Queue — serialize a named sub-action per queue name. */
  private async actionQueue(params: Record<string, unknown>, context?: Record<string, unknown>): Promise<void> {
    // @cogo/action-registry emits `queueId`; accept it as an alias for `queue`
    // so distinct registry-shaped queues serialize independently (not all collapse
    // into the shared `default`).
    const queueName = String(params.queue ?? params.queueId ?? "default")
    // priority (opt-in, default 0) — higher priority drains first among tasks queued
    // behind the in-flight task; equal priority keeps FIFO order (stable). A task
    // already shifted into flight cannot be reordered.
    const priority = Number(params.priority ?? 0)
    const { name: actionName, params: actionParams } = this.normalizeSubAction(params.action, params.params)
    if (!actionName) return
    if (!this.actionQueues.has(queueName)) this.actionQueues.set(queueName, [])
    const queue = this.actionQueues.get(queueName)!
    return new Promise<void>((resolve, reject) => {
      // Each task settles its OWN queueAction promise (resolve/reject) so a
      // failing sub-action surfaces to the caller instead of hanging it.
      const task = async () => {
        try {
          if (this.resolveBuiltin(actionName)) await this.dispatchSubAction(actionName, actionParams, context)
          resolve()
        } catch (e) {
          reject(e)
        }
      }
      // Insert ahead of the first strictly-lower-priority entry (stable for ties).
      let insertAt = 0
      while (insertAt < queue.length && queue[insertAt].priority >= priority) insertAt++
      queue.splice(insertAt, 0, { priority, task })
      // Start a single drain per queue. A `draining` flag (NOT queue.length) gates
      // this: while a task is awaiting it has already been shift()ed, so a length
      // check would let a concurrent call spin up a second drain and break
      // serialization. Swallow per-task rejections — each task settled its own
      // promise above — so one failure doesn't stop the rest of the queue.
      if (!this.queueDraining.has(queueName)) {
        this.queueDraining.add(queueName)
        void (async () => {
          try {
            while (queue.length > 0) {
              const next = queue.shift()!
              try { await next.task() } catch { /* settled via the task's own promise */ }
            }
          } finally {
            this.queueDraining.delete(queueName)
          }
        })()
      }
    })
  }

  /** Retry — re-run a named sub-action up to maxRetries with linear backoff. */
  private async actionRetry(params: Record<string, unknown>, context?: Record<string, unknown>): Promise<void> {
    const { name: actionName, params: actionParams } = this.normalizeSubAction(params.action, params.params)
    const maxRetries = Number(params.maxRetries ?? 3)
    // @cogo/action-registry names the backoff base `baseDelay`; accept it as an
    // alias for `delay` so configured retry spacing isn't silently lost.
    const delayMs = Number(params.delay ?? params.baseDelay ?? 1000)
    if (!actionName) return
    if (!this.resolveBuiltin(actionName)) return
    for (let i = 0; i <= maxRetries; i++) {
      try {
        await this.dispatchSubAction(actionName, actionParams, context)
        return
      } catch (e) {
        if (i === maxRetries) throw e
        await new Promise<void>((r) => setTimeout(r, delayMs * (i + 1)))
      }
    }
  }

  /** Throttle — run a named sub-action at most once per `interval` ms per key. */
  private async actionThrottle(params: Record<string, unknown>, context?: Record<string, unknown>): Promise<void> {
    const key = String(params.key ?? "default")
    const intervalMs = Number(params.interval ?? 1000)
    const { name: actionName, params: actionParams } = this.normalizeSubAction(params.action, params.params)
    if (!actionName) return
    const now = Date.now()
    const last = this.throttleTimestamps.get(key) ?? 0
    if (now - last < intervalMs) return
    this.throttleTimestamps.set(key, now)
    if (this.resolveBuiltin(actionName)) await this.dispatchSubAction(actionName, actionParams, context)
  }

  /**
   * Debounce — run a named sub-action only after `delay` ms of quiet per key.
   *
   * lodash-style edge controls (all opt-in; omitting them = the legacy trailing-only
   * behavior):
   *   leading  (default false) — also invoke immediately on the first call of a burst.
   *   trailing (default true)  — invoke after the quiet window. A single isolated call
   *                              with leading:true does NOT double-invoke (lodash rule).
   *   maxWait  (default 0=off) — force an invoke at least every maxWait ms during a
   *                              continuous call stream that never goes quiet.
   */
  private async actionDebounce(params: Record<string, unknown>, context?: Record<string, unknown>): Promise<void> {
    const key = String(params.key ?? "default")
    // TBox cschema names debounce's delay `wait`; accept it as an alias for `delay`.
    const delayMs = Number(params.delay ?? params.wait ?? 300)
    const leading = params.leading === true
    const trailing = params.trailing !== false
    const maxWaitMs = Number(params.maxWait ?? 0)
    const { name: actionName, params: actionParams } = this.normalizeSubAction(params.action, params.params)
    if (!actionName) return

    const runAction = async (): Promise<void> => {
      if (this.resolveBuiltin(actionName)) await this.dispatchSubAction(actionName, actionParams, context)
    }
    // Track this call as the latest for the key so a maxWait-forced invoke uses the
    // most-recent args (the trailing timer already binds the latest call's closure).
    this.actionDebounceLatestRun.set(key, runAction)

    // A pending trailing timer ⇒ we are mid-burst; the first call of a burst is the
    // leading edge.
    const existing = this.actionDebounceTimers.get(key)
    const isLeadingEdge = existing === undefined
    if (existing) {
      clearTimeout(existing)
      // Settle the superseded call so its awaiter (executeFlow / trace) doesn't hang
      // under rapid-event debounce — it just didn't run the action itself.
      this.actionDebounceResolvers.get(key)?.()
      this.actionDebounceResolvers.delete(key)
    }

    if (isLeadingEdge && leading) {
      // Served by the leading invoke; a lone call won't also fire on the trailing edge.
      this.actionDebouncePendingTrailing.delete(key)
    } else {
      // A call that still needs a trailing invoke to be served.
      this.actionDebouncePendingTrailing.add(key)
    }

    return new Promise<void>((resolve, reject) => {
      this.actionDebounceResolvers.set(key, resolve)

      // maxWait ceiling — armed once per burst; forces a trailing invoke even when
      // the stream never goes quiet, then ends the burst.
      if (maxWaitMs > 0 && !this.actionDebounceMaxTimers.has(key)) {
        this.actionDebounceMaxTimers.set(
          key,
          setTimeout(() => {
            this.actionDebounceMaxTimers.delete(key)
            const t = this.actionDebounceTimers.get(key)
            if (t) clearTimeout(t)
            this.actionDebounceTimers.delete(key)
            const r = this.actionDebounceResolvers.get(key)
            this.actionDebounceResolvers.delete(key)
            const pending = this.actionDebouncePendingTrailing.delete(key)
            // Use the LATEST call's invoker, not the leading call's stale closure.
            const latest = this.actionDebounceLatestRun.get(key) ?? runAction
            this.actionDebounceLatestRun.delete(key)
            void (async () => {
              try {
                if (trailing && pending) await latest()
                r?.()
              } catch (e) {
                reject(e)
              }
            })()
          }, maxWaitMs),
        )
      }

      // Trailing quiet-window timer.
      this.actionDebounceTimers.set(
        key,
        setTimeout(() => {
          this.actionDebounceTimers.delete(key)
          this.actionDebounceResolvers.delete(key)
          const maxT = this.actionDebounceMaxTimers.get(key)
          if (maxT) {
            clearTimeout(maxT)
            this.actionDebounceMaxTimers.delete(key)
          }
          const pending = this.actionDebouncePendingTrailing.delete(key)
          this.actionDebounceLatestRun.delete(key) // burst ended
          void (async () => {
            try {
              if (trailing && pending) await runAction()
              resolve()
            } catch (e) {
              // Surface a failing debounced sub-action instead of hanging the awaiter.
              reject(e)
            }
          })()
        }, delayMs),
      )

      // Leading-edge invoke LAST — after both timers are registered, so a second
      // call in the same tick sees the pending trailing timer and is treated as
      // part of the burst (not a fresh leading edge).
      if (isLeadingEdge && leading) {
        void runAction().catch(reject)
      }
    })
  }

  /** Transaction — run sub-actions; on failure run rollback actions; store result. */
  private async actionTransaction(params: Record<string, unknown>, context?: Record<string, unknown>): Promise<void> {
    // action-types.json (generation enum) requires `steps`; v1/@cogo/action-registry
    // use `actions` — accept either so generated transactions actually run.
    const actions = (params.actions ?? params.steps ?? []) as Array<{ action: string; params?: Record<string, unknown> }>
    const rollbackActions = (params.rollback ?? []) as Array<{ action: string; params?: Record<string, unknown> }>
    const resultTarget = String(params.resultTarget ?? "_results.transaction").replace(/^#/, "")
    const results: Array<{ action: string; status: string }> = []
    try {
      for (const sub of actions) {
        if (this.resolveBuiltin(sub.action)) {
          await this.dispatchSubAction(sub.action, (sub.params ?? {}) as Record<string, unknown>, context)
          results.push({ action: sub.action, status: "success" })
        }
      }
      this.state.setState(resultTarget, { success: true, results })
    } catch (e: any) {
      for (const rb of rollbackActions) {
        if (this.resolveBuiltin(rb.action)) {
          try { await this.dispatchSubAction(rb.action, (rb.params ?? {}) as Record<string, unknown>, context) } catch { /* ignore rollback errors */ }
        }
      }
      this.state.setState(resultTarget, { success: false, error: e?.message ?? String(e), results })
    }
  }

  /**
   * CSV export — headless-safe port of v1 actionCsvExport. Produces the
   * serialized CSV on `resultTarget` (always; downstream steps can read it) and,
   * when a platform is present, triggers a download via a `data:` URL. The v1
   * Blob/URL.createObjectURL path is replaced by `encodeURIComponent` (universal)
   * so no browser globals are required.
   */
  private actionCsvExport(params: Record<string, unknown>): void {
    const data = params.data as Record<string, unknown>[] | undefined
    if (!Array.isArray(data) || data.length === 0) return
    const columns = params.columns as Array<{ key: string; header: string }> | undefined
    const headers = columns ? columns.map((c) => c.header) : Object.keys(data[0])
    const keys = columns ? columns.map((c) => c.key) : Object.keys(data[0])
    const escapeField = (val: unknown): string => {
      const str = val == null ? "" : String(val)
      return /[",\n]/.test(str) ? `"${str.replace(/"/g, '""')}"` : str
    }
    const csv = [
      headers.map(escapeField).join(","),
      ...data.map((row) => keys.map((k) => escapeField(row[k])).join(",")),
    ].join("\n")
    const resultTarget = String(params.resultTarget ?? "_results.csvExport")
    this.state.setState(resultTarget, csv)
    if (!this.platform) return
    const filename = String(params.filename ?? "export.csv")
    // BOM prefix so spreadsheet apps detect UTF-8; kept out of the result payload.
    this.platform.download(`data:text/csv;charset=utf-8,${encodeURIComponent(String.fromCharCode(0xFEFF) + csv)}`, filename)
  }

  /**
   * JSON export — headless-safe port of v1 actionJsonExport. Same contract as
   * actionCsvExport: serialized payload on `resultTarget`, optional `data:` URL
   * download when a platform is present. A string `data` passes through unchanged.
   */
  private actionJsonExport(params: Record<string, unknown>): void {
    const raw = params.data
    if (raw == null) return
    const json = typeof raw === "string" ? raw : JSON.stringify(raw, null, 2)
    const resultTarget = String(params.resultTarget ?? "_results.jsonExport")
    this.state.setState(resultTarget, json)
    if (!this.platform) return
    const filename = String(params.filename ?? "export.json")
    this.platform.download(`data:application/json;charset=utf-8,${encodeURIComponent(json)}`, filename)
  }

  private async actionMediaCapture(params: Record<string, unknown>): Promise<void> {
    if (!this.platform) return
    const resultPath = String(params.resultPath ?? "_results.media")
    // source ("camera"|"screen") + quality (opt-in). `quality` may be a constraints
    // object ({width,height,frameRate}) or "high"/"low" (mapped to a resolution
    // preset). Omitting both ⇒ {audio, video:boolean} (current behavior).
    const source = params.source != null ? String(params.source) : undefined
    const quality = params.quality
    let video: boolean | Record<string, unknown> = params.type !== "audio"
    if (video) {
      if (quality && typeof quality === "object") video = quality as Record<string, unknown>
      else if (quality === "high") video = { width: 1920, height: 1080 }
      else if (quality === "low") video = { width: 640, height: 480 }
    }
    const constraints = {
      audio: Boolean(params.audio),
      video,
      ...(source ? { source: source as "camera" | "screen" } : {}),
    }
    // maxDuration (opt-in) — time-box a RECORDING via the platform's recordMedia
    // primitive (MediaRecorder on web), returning the recorded blob. Without
    // maxDuration (or without a recordMedia primitive) the runtime returns the live
    // MediaStream (current behavior).
    const maxDuration = Number(params.maxDuration ?? 0)
    if (maxDuration > 0 && this.platform.recordMedia) {
      try {
        const recorded = await this.platform.recordMedia(constraints, maxDuration)
        this.state.setState(resultPath, recorded)
      } catch {
        this.state.setState(resultPath, { error: "media_capture_failed" })
      }
      return
    }
    try {
      const media = await this.platform.getUserMedia(constraints)
      this.state.setState(resultPath, media)
    } catch {
      this.state.setState(resultPath, { error: "media_capture_failed" })
    }
  }

  private actionWebSocket(params: Record<string, unknown>): void {
    if (!this.platform) return
    const action = String(params.action ?? "connect")
    const url = String(params.url ?? "")
    const connectionId = String(params.connectionId ?? "default")
    const resultPath = `_results.ws.${connectionId}`

    if (action === "connect") {
      const ws = this.platform.createWebSocket(url)
      ws.onMessage((data) => this.state.setState(`${resultPath}.lastMessage`, data))
      ws.onClose(() => this.state.setState(`${resultPath}.connected`, false))
      this.state.setState(`${resultPath}.connected`, true)
      // Store send/close in state for later use
      this.state.setState(`${resultPath}.__ws`, ws)
    } else if (action === "send") {
      const ws = this.state.resolve(`#{${resultPath}.__ws}`) as any
      if (ws?.send) ws.send(String(params.data ?? ""))
    } else if (action === "close") {
      const ws = this.state.resolve(`#{${resultPath}.__ws}`) as any
      if (ws?.close) ws.close()
    }
  }
}

function diffTraceState(
  before: Record<string, unknown>,
  after: Record<string, unknown>,
  prefix = "",
): ActionFlowStateChange[] {
  const changes: ActionFlowStateChange[] = []
  const keys = new Set([...Object.keys(before), ...Object.keys(after)])
  for (const key of [...keys].sort()) {
    const path = prefix ? `${prefix}.${key}` : key
    const beforeValue = before[key]
    const afterValue = after[key]
    if (isPlainTraceObject(beforeValue) || isPlainTraceObject(afterValue)) {
      changes.push(...diffTraceState(
        isPlainTraceObject(beforeValue) ? beforeValue as Record<string, unknown> : {},
        isPlainTraceObject(afterValue) ? afterValue as Record<string, unknown> : {},
        path,
      ))
      continue
    }
    if (JSON.stringify(beforeValue) !== JSON.stringify(afterValue)) {
      changes.push({
        path,
        before: cloneTraceValue(beforeValue),
        after: cloneTraceValue(afterValue),
      })
    }
  }
  return changes
}

function isPlainTraceObject(value: unknown): boolean {
  return value !== null && typeof value === "object" && !Array.isArray(value)
}

function cloneTraceValue(value: unknown): unknown {
  if (value === undefined) return undefined
  try {
    return structuredClone(value)
  } catch {
    return JSON.parse(JSON.stringify(value))
  }
}

function errorToMessage(error: unknown): string {
  if (error instanceof Error) return error.message
  return String(error)
}

function isStopFlowSignal(error: unknown): boolean {
  return Boolean(
    error &&
    typeof error === "object" &&
    (error as Record<string, unknown>).__stopFlow === true,
  )
}

function isExecutionControlError(
  error: unknown,
): error is ActionFlowExecutionError {
  return (
    error instanceof ActionFlowExecutionError &&
    (error.code === "ACTIONFLOW_CANCELLED" ||
      error.code === "ACTIONFLOW_TIMEOUT")
  )
}

function executionAbortError(
  control: ActionFlowExecutionControl,
): ActionFlowExecutionError {
  if (control.signal.reason instanceof ActionFlowExecutionError) {
    return control.signal.reason
  }
  return new ActionFlowExecutionError(
    control.timedOut ? "ACTIONFLOW_TIMEOUT" : "ACTIONFLOW_CANCELLED",
    control.executionId,
    control.timedOut
      ? `ActionFlow execution "${control.executionId}" timed out.`
      : `ActionFlow execution "${control.executionId}" was cancelled.`,
  )
}

function executionStatusForError(error: unknown): ActionFlowExecutionStatus {
  if (error instanceof ActionFlowExecutionError) {
    if (error.code === "ACTIONFLOW_CANCELLED") return "cancelled"
    if (error.code === "ACTIONFLOW_TIMEOUT") return "timed-out"
  }
  return "error"
}

function duplicateExecutionError(
  policy: ResolvedActionFlowExecutionPolicy,
  duplicateOfExecutionId: string,
): ActionFlowExecutionError {
  return new ActionFlowExecutionError(
    "ACTIONFLOW_DUPLICATE",
    policy.executionId,
    `ActionFlow execution "${policy.executionId}" duplicates active or idempotent execution "${duplicateOfExecutionId}".`,
  )
}

function positiveInteger(value: unknown): number | undefined {
  const candidate = Number(value)
  return Number.isInteger(candidate) && candidate > 0 ? candidate : undefined
}

function readField(value: unknown, field: string): unknown {
  const keys = field.split(".").filter(Boolean)
  let current = value
  for (const key of keys) {
    if (current == null || typeof current !== "object") return undefined
    current = (current as Record<string, unknown>)[key]
  }
  return current
}

function buildTraceId(flow: Flow): string {
  const name = flow.name ?? "anonymous"
  return `actionflow:${slugTraceId(name)}:${flow.trigger}`
}

function buildExecutionId(flow: Flow): string {
  actionFlowExecutionSequence += 1
  return `actionflow-exec:${slugTraceId(flow.name ?? "anonymous")}:${Date.now().toString(36)}:${actionFlowExecutionSequence.toString(36)}`
}

function slugTraceId(value: string): string {
  return value
    .trim()
    .replace(/([a-z0-9])([A-Z])/g, "$1-$2")
    .toLowerCase()
    .replace(/[^a-z0-9_-]+/g, "-")
    .replace(/^-+|-+$/g, "") || "anonymous"
}
